import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Building2,
  FileText,
  Download,
  Users,
  Server,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Settings,
  Link2,
  Plus,
  Trash2,
  Edit,
  Send,
  UserPlus,
  Bell,
  Calendar,
  Euro,
  Clock,
  Shield,
  Upload,
  MapPin,
  Phone,
  Mail,
  Hash,
  Briefcase,
  ChevronRight,
  Save,
  Layers,
  SlidersHorizontal,
  ClipboardList,
  Plug,
  BookOpen,
  Search,
  Lock,
  CreditCard,
  Receipt,
  FolderOpen,
  Palette,
  CalendarDays,
  Timer,
  FileDown,
  Leaf,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  GripVertical,
  HardHat,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import Subscription from './Subscription';
import SousTraitantDocumentTypesConfig from '../components/SousTraitantDocumentTypesConfig';
import ChargesConfig from '../components/ChargesConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ============================================================
// NAVIGATION CONFIG
// module: null = toujours visible, string = module requis
// ============================================================
const NAV_SECTIONS = [
  {
    label: 'Structure',
    items: [
      { key: 'entreprise', label: 'Entreprise', icon: Building2, module: null },
      { key: 'departments', label: 'Départements', icon: Layers, module: null },
      { key: 'users', label: 'Utilisateurs', icon: Users, module: null },
      { key: 'roles', label: 'Rôles & Permissions', icon: Shield, module: null },
    ]
  },
  {
    label: 'Configuration RH',
    items: [
      { key: 'config', label: 'Convention & Paie', icon: Euro, module: 'payroll' },
      { key: 'habilitations', label: 'Habilitations', icon: ClipboardList, module: 'habilitations' },
      { key: 'interviews', label: 'Entretiens', icon: Calendar, module: 'entretiens' },
      { key: 'document-categories', label: 'Catégories documents', icon: FolderOpen, module: 'employees' },
      { key: 'custom-fields', label: 'Champs personnalisés', icon: SlidersHorizontal, module: null },
      { key: 'expenses-config', label: 'Notes de frais', icon: Receipt, module: 'expenses' },
      { key: 'absence-types', label: 'Types d\'absences', icon: Calendar, module: 'conges' },
      { key: 'creneau-shortcuts', label: 'Raccourcis créneaux', icon: Timer, module: 'planning' },
      { key: 'periodes-paie', label: 'Périodes de paie', icon: CalendarDays, module: 'payroll' },
      { key: 'payroll-export', label: 'Export paie', icon: FileDown, module: 'payroll' },
      { key: 'rse-categories', label: 'Catégories RSE', icon: Leaf, module: 'rse' },
      { key: 'sous_traitants', label: 'Sous-traitants', icon: HardHat, module: 'sous_traitants' },
      { key: 'charges', label: 'Charges sociales', icon: Percent, module: 'reporting' },
    ]
  },
  {
    label: 'Système',
    items: [
      { key: 'subscription', label: 'Abonnement & Factures', icon: CreditCard, module: null },
      { key: 'notifications', label: 'Notifications', icon: Bell, module: null },
      { key: 'integrations', label: 'Intégrations', icon: Plug, module: 'payroll' },
      { key: 'registre', label: 'Registre & Export', icon: BookOpen, module: null },
    ]
  }
];

// ============================================================
// SUB-COMPONENTS
// ============================================================

// --- Habilitations Config ---
const HabilitationsConfig = () => {
  const [habilitationTypes, setHabilitationTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [newType, setNewType] = useState({ code: '', label: '', alert_days: 90 });

  useEffect(() => { fetchHabilitationTypes(); }, []);

  const fetchHabilitationTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/habilitation-types`);
      setHabilitationTypes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des types d\'habilitations');
    } finally { setLoading(false); }
  };

  const handleAddType = async () => {
    if (!newType.code || !newType.label) { toast.error('Veuillez remplir le code et le libellé'); return; }
    try {
      await axios.post(`${API_URL}/admin/habilitation-types`, newType);
      toast.success('Type d\'habilitation ajouté');
      setShowAddDialog(false);
      setNewType({ code: '', label: '', alert_days: 90 });
      fetchHabilitationTypes();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout'); }
  };

  const handleUpdateType = async (code, alertDays) => {
    try {
      const updatedTypes = { ...habilitationTypes };
      updatedTypes[code].alert_days = alertDays;
      await axios.put(`${API_URL}/admin/habilitation-types`, { types: updatedTypes });
      toast.success('Délai d\'alerte mis à jour');
      setHabilitationTypes(updatedTypes);
      setEditingType(null);
    } catch (error) { toast.error('Erreur lors de la mise à jour'); }
  };

  const handleDeleteType = async (code) => {
    if (!window.confirm(`Supprimer le type d'habilitation "${habilitationTypes[code]?.label}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/habilitation-types/${code}`);
      toast.success('Type supprimé');
      fetchHabilitationTypes();
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  if (loading) return <LoadingSpinner />;

  const typeEntries = Object.entries(habilitationTypes);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Types d'habilitations"
        description="Configurez les types d'habilitations et leurs délais d'alerte avant expiration"
        action={
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" />Ajouter un type</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau type d'habilitation</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Code (identifiant unique)</Label>
                  <Input value={newType.code} onChange={(e) => setNewType({ ...newType, code: e.target.value.toLowerCase().replace(/\s/g, '_') })} placeholder="ex: permis_ce" />
                </div>
                <div className="space-y-2">
                  <Label>Libellé</Label>
                  <Input value={newType.label} onChange={(e) => setNewType({ ...newType, label: e.target.value })} placeholder="ex: Permis CE" />
                </div>
                <div className="space-y-2">
                  <Label>Délai d'alerte (jours avant expiration)</Label>
                  <Input type="number" value={newType.alert_days} onChange={(e) => setNewType({ ...newType, alert_days: parseInt(e.target.value) || 90 })} min={1} />
                  <p className="text-xs text-slate-500">Une alerte sera générée ce nombre de jours avant l'expiration</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
                  <Button onClick={handleAddType} className="bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <Card>
        <CardContent className="p-0">
          {typeEntries.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucun type d'habilitation configuré</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead>Délai d'alerte</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {typeEntries.map(([code, config]) => (
                  <TableRow key={code}>
                    <TableCell className="font-mono text-sm">{code}</TableCell>
                    <TableCell className="font-medium">{config.label}</TableCell>
                    <TableCell>
                      {editingType === code ? (
                        <div className="flex items-center gap-2">
                          <Input type="number" className="w-20" defaultValue={config.alert_days} min={1} id={`edit-days-${code}`} />
                          <Button size="sm" onClick={() => { const input = document.getElementById(`edit-days-${code}`); handleUpdateType(code, parseInt(input.value) || config.alert_days); }} className="bg-[#0F5156] hover:bg-[#0d464a]">OK</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingType(null)}>Annuler</Button>
                        </div>
                      ) : (
                        <Badge variant="outline" className="cursor-pointer" onClick={() => setEditingType(code)}>
                          <Clock className="w-3 h-3 mr-1" />{config.alert_days} jours
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditingType(code)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteType(code)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// --- Document Categories Config ---
const DocumentCategoriesConfig = () => {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [newCat, setNewCat] = useState({ code: '', label: '', color: '#94A3B8' });

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/document-categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    } finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newCat.code || !newCat.label) { toast.error('Veuillez remplir le code et le libellé'); return; }
    try {
      await axios.post(`${API_URL}/admin/document-categories`, newCat);
      toast.success('Catégorie ajoutée');
      setShowAddDialog(false);
      setNewCat({ code: '', label: '', color: '#94A3B8' });
      fetchCategories();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout'); }
  };

  const handleUpdate = async (code) => {
    try {
      await axios.put(`${API_URL}/admin/document-categories/${code}`, {
        label: categories[code].label,
        color: categories[code].color
      });
      toast.success('Catégorie mise à jour');
      setEditingCat(null);
    } catch (error) { toast.error('Erreur lors de la mise à jour'); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Supprimer la catégorie "${categories[code]?.label}" ? Les documents existants garderont leur catégorie.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/document-categories/${code}`);
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  // Déplace une catégorie vers le haut ou le bas et sauvegarde l'ordre
  const handleMove = async (code, direction) => {
    const codes = Object.keys(categories);
    const idx = codes.indexOf(code);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= codes.length) return;
    [codes[idx], codes[target]] = [codes[target], codes[idx]];
    // Mise à jour optimiste de l'affichage
    const reordered = {};
    codes.forEach(c => { reordered[c] = categories[c]; });
    setCategories(reordered);
    try {
      await axios.put(`${API_URL}/admin/document-categories-order`, { order: codes });
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement de l'ordre");
      fetchCategories();
    }
  };

  if (loading) return <LoadingSpinner />;

  const catEntries = Object.entries(categories);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Catégories de documents"
        description="Personnalisez les catégories disponibles dans le dossier RH des employés"
        action={
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" />Ajouter une catégorie</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle catégorie de document</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Code (identifiant unique)</Label>
                  <Input value={newCat.code} onChange={(e) => setNewCat({ ...newCat, code: e.target.value.toLowerCase().replace(/\s/g, '_') })} placeholder="ex: bulletin_paie" />
                </div>
                <div className="space-y-2">
                  <Label>Libellé</Label>
                  <Input value={newCat.label} onChange={(e) => setNewCat({ ...newCat, label: e.target.value })} placeholder="ex: Bulletin de paie" />
                </div>
                <div className="space-y-2">
                  <Label>Couleur</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={newCat.color} onChange={(e) => setNewCat({ ...newCat, color: e.target.value })} className="w-10 h-10 rounded cursor-pointer border" />
                    <Input value={newCat.color} onChange={(e) => setNewCat({ ...newCat, color: e.target.value })} className="w-32 font-mono text-sm" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
                  <Button onClick={handleAdd} className="bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <Card>
        <CardContent className="p-0">
          {catEntries.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucune catégorie configurée</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Couleur</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catEntries.map(([code, config], idx) => (
                  <TableRow key={code}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <div className="flex flex-col">
                          <Button variant="ghost" size="icon" className="h-5 w-5" disabled={idx === 0} onClick={() => handleMove(code, 'up')} title="Monter">
                            <ChevronUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-5 w-5" disabled={idx === catEntries.length - 1} onClick={() => handleMove(code, 'down')} title="Descendre">
                            <ChevronDown className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: config.color || '#94A3B8' }} />
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{code}</TableCell>
                    <TableCell>
                      {editingCat === code ? (
                        <div className="flex items-center gap-2">
                          <Input value={config.label} onChange={(e) => { const c = { ...categories }; c[code].label = e.target.value; setCategories(c); }} className="w-48" />
                          <input type="color" value={config.color || '#94A3B8'} onChange={(e) => { const c = { ...categories }; c[code].color = e.target.value; setCategories(c); }} className="w-8 h-8 rounded cursor-pointer border" />
                          <Button size="sm" onClick={() => handleUpdate(code)} className="bg-[#0F5156] hover:bg-[#0d464a]">OK</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingCat(null); fetchCategories(); }}>Annuler</Button>
                        </div>
                      ) : (
                        <span className="font-medium">{config.label}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditingCat(code)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(code)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// --- Interview Types Config ---
const InterviewTypesConfig = () => {
  const [interviewTypes, setInterviewTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [newType, setNewType] = useState({ code: '', label: '', description: '', recurrence_mois: 12, alerte_jours_avant: 30, obligatoire: false, duree_default: 60 });

  useEffect(() => { fetchInterviewTypes(); }, []);

  const fetchInterviewTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/interview-types`);
      setInterviewTypes(response.data || {});
    } catch (error) { console.error('Error fetching interview types:', error); }
    finally { setLoading(false); }
  };

  const handleAddType = async () => {
    if (!newType.code || !newType.label) { toast.error('Code et libellé requis'); return; }
    try {
      await axios.post(`${API_URL}/admin/interview-types`, newType);
      toast.success('Type d\'entretien ajouté');
      setShowAddDialog(false);
      setNewType({ code: '', label: '', description: '', recurrence_mois: 12, alerte_jours_avant: 30, obligatoire: false, duree_default: 60 });
      fetchInterviewTypes();
    } catch (error) { toast.error('Erreur lors de l\'ajout'); }
  };

  const handleUpdateType = async () => {
    if (!editingType) return;
    const updatedTypes = { ...interviewTypes };
    updatedTypes[editingType.code] = { label: editingType.label, description: editingType.description, recurrence_mois: editingType.recurrence_mois, alerte_jours_avant: editingType.alerte_jours_avant, obligatoire: editingType.obligatoire, duree_default: editingType.duree_default };
    try {
      await axios.put(`${API_URL}/admin/interview-types`, updatedTypes);
      toast.success('Type d\'entretien modifié');
      setEditingType(null);
      fetchInterviewTypes();
    } catch (error) { toast.error('Erreur lors de la modification'); }
  };

  const handleDeleteType = async (code) => {
    if (!window.confirm('Supprimer ce type d\'entretien ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/interview-types/${code}`);
      toast.success('Type d\'entretien supprimé');
      fetchInterviewTypes();
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Types d'entretiens"
        description="Configurez les types d'entretiens avec récurrence et alertes"
        action={
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" />Ajouter un type</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau type d'entretien</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code *</Label>
                    <Input value={newType.code} onChange={(e) => setNewType({ ...newType, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })} placeholder="ex: annuel" />
                  </div>
                  <div className="space-y-2">
                    <Label>Libellé *</Label>
                    <Input value={newType.label} onChange={(e) => setNewType({ ...newType, label: e.target.value })} placeholder="ex: Entretien annuel" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input value={newType.description} onChange={(e) => setNewType({ ...newType, description: e.target.value })} placeholder="Description du type d'entretien" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Récurrence (mois)</Label>
                    <Input type="number" min="0" value={newType.recurrence_mois} onChange={(e) => setNewType({ ...newType, recurrence_mois: parseInt(e.target.value) || 0 })} />
                    <p className="text-xs text-slate-500">0 = pas de récurrence</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Alerte (jours avant)</Label>
                    <Input type="number" min="0" value={newType.alerte_jours_avant} onChange={(e) => setNewType({ ...newType, alerte_jours_avant: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Durée par défaut (min)</Label>
                    <Input type="number" min="15" step="15" value={newType.duree_default} onChange={(e) => setNewType({ ...newType, duree_default: parseInt(e.target.value) || 60 })} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={newType.obligatoire} onCheckedChange={(checked) => setNewType({ ...newType, obligatoire: checked })} />
                  <Label>Entretien obligatoire</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
                  <Button onClick={handleAddType} className="bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <Card>
        <CardContent className="p-0">
          {Object.keys(interviewTypes).length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucun type d'entretien configuré</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="hidden md:table-cell">Récurrence</TableHead>
                  <TableHead className="hidden md:table-cell">Alerte</TableHead>
                  <TableHead className="hidden lg:table-cell">Durée</TableHead>
                  <TableHead>Obligatoire</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(interviewTypes).map(([code, config]) => (
                  <TableRow key={code}>
                    <TableCell className="font-mono text-sm">{code}</TableCell>
                    <TableCell>{config.label}</TableCell>
                    <TableCell className="hidden md:table-cell">{config.recurrence_mois > 0 ? `${config.recurrence_mois} mois` : '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">{config.alerte_jours_avant > 0 ? `${config.alerte_jours_avant}j avant` : '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell">{config.duree_default || 60} min</TableCell>
                    <TableCell>
                      {config.obligatoire ? <Badge className="bg-red-100 text-red-800">Obligatoire</Badge> : <Badge variant="outline">Optionnel</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setEditingType({ code, ...config })}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteType(code)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editingType} onOpenChange={(open) => { if (!open) setEditingType(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier le type d'entretien</DialogTitle></DialogHeader>
          {editingType && (
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Libellé</Label><Input value={editingType.label} onChange={(e) => setEditingType({ ...editingType, label: e.target.value })} /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={editingType.description || ''} onChange={(e) => setEditingType({ ...editingType, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label>Récurrence (mois)</Label><Input type="number" min="0" value={editingType.recurrence_mois} onChange={(e) => setEditingType({ ...editingType, recurrence_mois: parseInt(e.target.value) || 0 })} /></div>
                <div className="space-y-2"><Label>Alerte (jours avant)</Label><Input type="number" min="0" value={editingType.alerte_jours_avant} onChange={(e) => setEditingType({ ...editingType, alerte_jours_avant: parseInt(e.target.value) || 0 })} /></div>
                <div className="space-y-2"><Label>Durée par défaut (min)</Label><Input type="number" min="15" step="15" value={editingType.duree_default || 60} onChange={(e) => setEditingType({ ...editingType, duree_default: parseInt(e.target.value) || 60 })} /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editingType.obligatoire} onCheckedChange={(checked) => setEditingType({ ...editingType, obligatoire: checked })} />
                <Label>Entretien obligatoire</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingType(null)}>Annuler</Button>
                <Button onClick={handleUpdateType} className="bg-[#0F5156] hover:bg-[#0d464a]">Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Custom Fields Config ---
// --- Expenses Workflow Config ---
const EXPENSE_ASSIGNABLE_ROLES = [
  { value: 'admin', label: 'Administrateur' },
  { value: 'rh', label: 'Ressources Humaines' },
  { value: 'comptabilite', label: 'Comptabilité' },
  { value: 'manager', label: 'Manager' },
  { value: 'exploitation', label: 'Exploitation' }
];

const ExpensesWorkflowConfig = () => {
  const [workflow, setWorkflow] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchWorkflow(); }, []);

  const fetchWorkflow = async () => {
    try {
      const [wfRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/expenses/workflow-config`),
        axios.get(`${API_URL}/structure/users`).catch(() => ({ data: [] }))
      ]);
      setWorkflow(wfRes.data);
      setUsers((usersRes.data || []).filter(u => u.is_active !== false));
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration');
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    const invalid = (workflow.steps || []).find(s => s.assignment?.type === 'users' && !(s.assignment.user_ids || []).length);
    if (invalid) { toast.error(`L'étape « ${invalid.label || 'sans nom'} » doit désigner au moins un utilisateur`); return; }
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/expenses/workflow-config`, workflow);
      if (response.data?.workflow) setWorkflow(response.data.workflow);
      toast.success('Configuration sauvegardée');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const renumber = (steps) => steps.map((s, i) => ({ ...s, order: i + 1 }));

  const handleAddStep = () => {
    const steps = [...(workflow.steps || [])];
    steps.push({
      id: `step-${Date.now()}`,
      order: steps.length + 1,
      label: 'Nouvelle étape',
      assignment: { type: 'role', role: 'rh', user_ids: [] },
      can_edit_amount: false
    });
    setWorkflow({ ...workflow, steps: renumber(steps) });
  };

  const handleRemoveStep = (index) => {
    const steps = workflow.steps.filter((_, i) => i !== index);
    setWorkflow({ ...workflow, steps: renumber(steps) });
  };

  const handleMoveStep = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= workflow.steps.length) return;
    const steps = [...workflow.steps];
    [steps[index], steps[target]] = [steps[target], steps[index]];
    setWorkflow({ ...workflow, steps: renumber(steps) });
  };

  const handleStepChange = (index, field, value) => {
    const steps = [...workflow.steps];
    steps[index] = { ...steps[index], [field]: value };
    setWorkflow({ ...workflow, steps });
  };

  const handleAssignmentChange = (index, patch) => {
    const steps = [...workflow.steps];
    const current = steps[index].assignment || { type: 'role', role: 'rh', user_ids: [] };
    steps[index] = { ...steps[index], assignment: { ...current, ...patch } };
    setWorkflow({ ...workflow, steps });
  };

  const toggleAssignedUser = (index, userId, checked) => {
    const current = workflow.steps[index].assignment?.user_ids || [];
    const user_ids = checked ? [...new Set([...current, userId])] : current.filter(id => id !== userId);
    handleAssignmentChange(index, { user_ids });
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F5156]"></div></div>;
  if (!workflow) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Workflow de validation</h2>
          <p className="text-sm text-slate-500 mt-1">Configurez le processus de validation des notes de frais</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {/* Options globales */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Options générales</CardTitle>
          <CardDescription>Auto-approbation, confirmation finale et notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Seuil d'auto-approbation (en euros, laisser vide pour désactiver)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Désactivé"
              value={workflow.auto_approve_below ?? ''}
              onChange={(e) => setWorkflow({
                ...workflow,
                auto_approve_below: e.target.value ? parseFloat(e.target.value) : null
              })}
            />
            <p className="text-xs text-slate-500">Les notes de frais dont le montant TTC est inférieur ou égal à ce seuil sont approuvées automatiquement.</p>
          </div>
          <div className="flex items-start justify-between gap-4 p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
            <div>
              <p className="text-sm font-medium">Confirmation finale par le déposant</p>
              <p className="text-xs text-slate-500 mt-0.5">Si le montant a été corrigé pendant la validation, le déposant doit confirmer la note avant qu'elle soit approuvée.</p>
            </div>
            <Switch
              checked={!!workflow.confirmation_finale_deposant}
              onCheckedChange={(v) => setWorkflow({ ...workflow, confirmation_finale_deposant: v })}
            />
          </div>
          <div className="flex items-start justify-between gap-4 p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
            <div>
              <p className="text-sm font-medium">Notifier par e-mail</p>
              <p className="text-xs text-slate-500 mt-0.5">En plus des notifications dans l'application, envoyer un e-mail aux validateurs et au déposant à chaque étape (nécessite une configuration SMTP).</p>
            </div>
            <Switch
              checked={workflow.notifier_email !== false}
              onCheckedChange={(v) => setWorkflow({ ...workflow, notifier_email: v })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Etapes de validation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Étapes de validation</CardTitle>
              <CardDescription>Les notes de frais passent par chaque étape, dans l'ordre. À chaque étape, les personnes assignées reçoivent une notification.</CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={handleAddStep}>
              <Plus className="w-4 h-4 mr-2" /> Ajouter une étape
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {workflow.steps && workflow.steps.length > 0 ? (
            <div className="space-y-3">
              {workflow.steps.map((step, index) => {
                const assignment = step.assignment || { type: 'role', role: 'rh', user_ids: [] };
                return (
                  <div key={step.id || index} className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveStep(index, -1)} disabled={index === 0} title="Monter">
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleMoveStep(index, 1)} disabled={index === workflow.steps.length - 1} title="Descendre">
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <span className="text-sm font-bold text-[#0F5156] min-w-[24px]">{index + 1}</span>
                      <Input
                        className="flex-1"
                        value={step.label}
                        onChange={(e) => handleStepChange(index, 'label', e.target.value)}
                        placeholder="Nom de l'étape"
                      />
                      <Select
                        value={assignment.type}
                        onValueChange={(v) => handleAssignmentChange(index, { type: v, role: v === 'role' ? (assignment.role || 'rh') : null, user_ids: v === 'users' ? (assignment.user_ids || []) : [] })}
                      >
                        <SelectTrigger className="w-56">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="role">Par rôle</SelectItem>
                          <SelectItem value="users">Utilisateurs désignés</SelectItem>
                          <SelectItem value="responsable_departement">Responsable du département</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" onClick={() => handleRemoveStep(index)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="pl-12 space-y-3">
                      {assignment.type === 'role' && (
                        <div className="flex items-center gap-3">
                          <Label className="text-xs text-slate-500 w-28">Rôle validateur</Label>
                          <Select value={assignment.role || 'rh'} onValueChange={(v) => handleAssignmentChange(index, { role: v })}>
                            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {EXPENSE_ASSIGNABLE_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-slate-500">Tous les utilisateurs actifs ayant ce rôle</span>
                        </div>
                      )}
                      {assignment.type === 'users' && (
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Utilisateurs assignés</Label>
                          {users.length === 0 ? (
                            <p className="text-xs text-slate-500">Aucun utilisateur disponible</p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md p-2">
                              {users.map(u => (
                                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                  <Checkbox
                                    checked={(assignment.user_ids || []).includes(u.id)}
                                    onCheckedChange={(checked) => toggleAssignedUser(index, u.id, !!checked)}
                                  />
                                  <span className="truncate">{u.prenom} {u.nom} <span className="text-xs text-slate-400">({u.role})</span></span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {assignment.type === 'responsable_departement' && (
                        <p className="text-xs text-slate-500">Le responsable du département du déposant (défini dans Administration › Départements). À défaut, les administrateurs de la structure.</p>
                      )}
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={!!step.can_edit_amount}
                          onCheckedChange={(checked) => handleStepChange(index, 'can_edit_amount', !!checked)}
                        />
                        Peut corriger le montant
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">Aucune étape configurée. Les notes seront automatiquement approuvées.</p>
          )}
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Catégories de dépenses</CardTitle>
          <CardDescription>Categories disponibles pour les notes de frais</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {(workflow.categories || ['transport', 'repas', 'hebergement', 'materiel', 'carburant', 'peage', 'parking', 'autre']).map((cat) => (
              <Badge key={cat} variant="secondary" className="capitalize">{cat}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const CustomFieldsConfig = () => {
  const [customFields, setCustomFields] = useState({ identity: [], contract: [], other: [] });
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedSection, setSelectedSection] = useState('identity');
  const [newField, setNewField] = useState({ code: '', label: '', type: 'text', required: false, placeholder: '', options: '' });

  useEffect(() => { fetchCustomFields(); }, []);

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/custom-fields`);
      setCustomFields(response.data || { identity: [], contract: [], other: [] });
    } catch (error) { console.error('Error fetching custom fields:', error); }
    finally { setLoading(false); }
  };

  const handleAddField = async () => {
    if (!newField.code || !newField.label) { toast.error('Code et libellé requis'); return; }
    try {
      const fieldData = { ...newField, section: selectedSection, options: newField.type === 'select' ? newField.options.split(',').map(o => o.trim()).filter(o => o) : [] };
      await axios.post(`${API_URL}/admin/custom-fields`, fieldData);
      toast.success('Champ personnalisé ajouté');
      setShowAddDialog(false);
      setNewField({ code: '', label: '', type: 'text', required: false, placeholder: '', options: '' });
      fetchCustomFields();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout'); }
  };

  const handleDeleteField = async (section, fieldCode) => {
    if (!window.confirm('Supprimer ce champ personnalisé ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/custom-fields/${section}/${fieldCode}`);
      toast.success('Champ supprimé');
      fetchCustomFields();
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  const handleToggleRequired = async (section, fieldCode, currentRequired) => {
    try {
      await axios.put(`${API_URL}/admin/custom-fields/${section}/${fieldCode}`, { required: !currentRequired });
      fetchCustomFields();
    } catch (error) { toast.error('Erreur lors de la mise à jour'); }
  };

  const fieldTypes = [
    { value: 'text', label: 'Texte' }, { value: 'number', label: 'Nombre' }, { value: 'date', label: 'Date' },
    { value: 'select', label: 'Liste déroulante' }, { value: 'checkbox', label: 'Case à cocher' },
    { value: 'textarea', label: 'Zone de texte' }, { value: 'email', label: 'Email' }, { value: 'phone', label: 'Téléphone' }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Champs personnalisés"
        description="Ajoutez des champs supplémentaires aux fiches employés"
        action={
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" />Ajouter un champ</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau champ personnalisé</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Section</Label>
                  <Select value={selectedSection} onValueChange={setSelectedSection}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="identity">Identité</SelectItem>
                      <SelectItem value="contract">Contrat</SelectItem>
                      <SelectItem value="other">Autres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Code *</Label><Input value={newField.code} onChange={(e) => setNewField({ ...newField, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })} placeholder="ex: numero_badge" /></div>
                  <div className="space-y-2"><Label>Libellé *</Label><Input value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} placeholder="ex: Numéro de badge" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={newField.type} onValueChange={(value) => setNewField({ ...newField, type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{fieldTypes.map(type => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Placeholder</Label><Input value={newField.placeholder} onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })} placeholder="Texte d'aide" /></div>
                </div>
                {newField.type === 'select' && (
                  <div className="space-y-2"><Label>Options (séparées par des virgules)</Label><Input value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} placeholder="Option 1, Option 2, Option 3" /></div>
                )}
                <div className="flex items-center gap-2">
                  <Checkbox checked={newField.required} onCheckedChange={(checked) => setNewField({ ...newField, required: checked })} />
                  <Label>Champ obligatoire</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
                  <Button onClick={handleAddField} className="bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="identity">
            <TabsList>
              <TabsTrigger value="identity">Identité ({customFields.identity?.length || 0})</TabsTrigger>
              <TabsTrigger value="contract">Contrat ({customFields.contract?.length || 0})</TabsTrigger>
              <TabsTrigger value="other">Autres ({customFields.other?.length || 0})</TabsTrigger>
            </TabsList>
            {['identity', 'contract', 'other'].map(section => (
              <TabsContent key={section} value={section}>
                {(!customFields[section] || customFields[section].length === 0) ? (
                  <p className="text-center text-slate-500 py-8">Aucun champ personnalisé dans cette section</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead><TableHead>Libellé</TableHead><TableHead>Type</TableHead><TableHead>Obligatoire</TableHead><TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customFields[section].map((field) => (
                        <TableRow key={field.code}>
                          <TableCell className="font-mono text-sm">{field.code}</TableCell>
                          <TableCell>{field.label}</TableCell>
                          <TableCell><Badge variant="outline">{fieldTypes.find(t => t.value === field.type)?.label || field.type}</Badge></TableCell>
                          <TableCell><Switch checked={field.required} onCheckedChange={() => handleToggleRequired(section, field.code, field.required)} /></TableCell>
                          <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handleDeleteField(section, field.code)}><Trash2 className="w-4 h-4 text-red-500" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// ABSENCE TYPES CONFIG
// ============================================================
const AbsenceTypesConfig = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [newType, setNewType] = useState({ code: '', label: '', decompte_solde: false, justificatif_requis: false, color: '#3B82F6', actif: true, validation_requise: true, roles_saisie: [] });

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/absence-types`);
        setTypes(res.data);
      } catch { toast.error('Erreur chargement types d\'absences'); }
      finally { setLoading(false); }
    };
    fetchTypes();
  }, []);

  const saveTypes = async (updatedTypes) => {
    try {
      await axios.put(`${API_URL}/admin/absence-types`, { types: updatedTypes });
      setTypes(updatedTypes);
      toast.success('Types d\'absences mis à jour');
    } catch { toast.error('Erreur de sauvegarde'); }
  };

  const handleAdd = () => {
    if (!newType.code || !newType.label) { toast.error('Code et label requis'); return; }
    if (types.some(t => t.code === newType.code)) { toast.error('Ce code existe déjà'); return; }
    const updated = [...types, { ...newType }];
    saveTypes(updated);
    setNewType({ code: '', label: '', decompte_solde: false, justificatif_requis: false, color: '#3B82F6', actif: true, validation_requise: true, roles_saisie: [] });
    setShowAddDialog(false);
  };

  const handleToggle = (code, field) => {
    const updated = types.map(t => t.code === code ? { ...t, [field]: !t[field] } : t);
    saveTypes(updated);
  };

  const handleDelete = (code) => {
    if (!window.confirm('Supprimer ce type d\'absence ?')) return;
    saveTypes(types.filter(t => t.code !== code));
  };

  const handleUpdateLabel = (code, label) => {
    const updated = types.map(t => t.code === code ? { ...t, label } : t);
    saveTypes(updated);
  };

  const handleUpdateColor = (code, color) => {
    const updated = types.map(t => t.code === code ? { ...t, color } : t);
    saveTypes(updated);
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F5156]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Types d'absences</h2>
          <p className="text-sm text-slate-500">Configurez les types d'absences disponibles pour votre structure</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5156] hover:bg-[#0a3d41]"><Plus className="h-4 w-4 mr-2" />Ajouter un type</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau type d'absence</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Code *</Label><Input value={newType.code} onChange={e => setNewType(p => ({ ...p, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))} placeholder="ex: conge_parental" /></div>
              <div><Label>Label *</Label><Input value={newType.label} onChange={e => setNewType(p => ({ ...p, label: e.target.value }))} placeholder="ex: Congé parental" /></div>
              <div><Label>Couleur</Label><Input type="color" value={newType.color} onChange={e => setNewType(p => ({ ...p, color: e.target.value }))} className="h-10 w-20" /></div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={newType.decompte_solde} onCheckedChange={v => setNewType(p => ({ ...p, decompte_solde: v }))} />Décompte du solde</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={newType.justificatif_requis} onCheckedChange={v => setNewType(p => ({ ...p, justificatif_requis: v }))} />Justificatif requis</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={newType.validation_requise !== false} onCheckedChange={v => setNewType(p => ({ ...p, validation_requise: v }))} />Validation requise</label>
              </div>
              {!newType.validation_requise && (
                <div>
                  <Label>Rôles autorisés pour saisie directe</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {['admin', 'rh', 'manager'].map(role => (
                      <label key={role} className="flex items-center gap-1 text-sm">
                        <Checkbox
                          checked={(newType.roles_saisie || []).includes(role)}
                          onCheckedChange={v => setNewType(p => ({
                            ...p,
                            roles_saisie: v ? [...(p.roles_saisie || []), role] : (p.roles_saisie || []).filter(r => r !== role)
                          }))}
                        />
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
                <Button onClick={handleAdd} className="bg-[#0F5156]">Ajouter</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Card>
        <CardContent className="py-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Couleur</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Décompte solde</TableHead>
                <TableHead>Justificatif</TableHead>
                <TableHead>Validation</TableHead>
                <TableHead>Actif</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {types.map(t => (
                <TableRow key={t.code}>
                  <TableCell><input type="color" value={t.color || '#3B82F6'} onChange={e => handleUpdateColor(t.code, e.target.value)} className="w-8 h-8 rounded cursor-pointer" /></TableCell>
                  <TableCell><code className="text-xs bg-slate-100 px-2 py-1 rounded">{t.code}</code></TableCell>
                  <TableCell><Input value={t.label} onChange={e => handleUpdateLabel(t.code, e.target.value)} className="w-48 h-8" /></TableCell>
                  <TableCell><Switch checked={t.decompte_solde} onCheckedChange={() => handleToggle(t.code, 'decompte_solde')} /></TableCell>
                  <TableCell><Switch checked={t.justificatif_requis} onCheckedChange={() => handleToggle(t.code, 'justificatif_requis')} /></TableCell>
                  <TableCell><Switch checked={t.validation_requise !== false} onCheckedChange={() => handleToggle(t.code, 'validation_requise')} /></TableCell>
                  <TableCell><Switch checked={t.actif !== false} onCheckedChange={() => handleToggle(t.code, 'actif')} /></TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={() => handleDelete(t.code)}><Trash2 className="w-4 h-4 text-red-500" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// --- Shared UI Helpers ---
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-32">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F5156]"></div>
  </div>
);

const SectionHeader = ({ title, description, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div>
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
      {description && <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>}
    </div>
    {action}
  </div>
);

const InfoRow = ({ icon: Icon, label, value, editable, onChange, type = 'text', placeholder }) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0">
    <Icon className="w-4 h-4 text-slate-400 mt-2.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      {editable ? (
        <Input value={value || ''} onChange={onChange} type={type} placeholder={placeholder} className="h-9" />
      ) : (
        <p className="text-sm font-medium text-slate-900 dark:text-white">{value || <span className="text-slate-400 italic">Non renseigné</span>}</p>
      )}
    </div>
  </div>
);

// --- Configuration Export Paie ---
const PayrollExportConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const ALL_COLUMNS = [
    { key: 'employee', label: 'Employé' },
    { key: 'matricule', label: 'Matricule' },
    { key: 'absences', label: 'Absences (type / jours)' },
    { key: 'jours_deduits', label: 'Jours à déduire' },
    { key: 'paniers', label: 'Paniers Repas' },
    { key: 'hs_25', label: 'HS 25%' },
    { key: 'hs_50', label: 'HS 50%' },
    { key: 'h_nuit', label: 'H. Nuit' },
    { key: 'h_dimanche', label: 'H. Dimanche' },
    { key: 'jf_travailles', label: 'JF Travaillés' },
    { key: 'primes', label: 'Primes' },
    { key: 'acomptes', label: 'Acomptes (date, montant, motif)' },
    { key: 'saisies', label: 'Saisies sur salaire' },
    { key: 'statut', label: 'Statut' },
    { key: 'commentaire', label: 'Commentaire pour la paie (toujours exporté)' }
  ];

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/payroll-export-config`);
        setConfig(res.data);
      } catch {
        setConfig({
          columns: ALL_COLUMNS.map(c => c.key),
          orientation: 'landscape',
          include_details: true
        });
      }
      finally { setLoading(false); }
    };
    fetchConfig();
  }, []);

  const saveConfig = async (newConfig) => {
    try {
      await axios.put(`${API_URL}/admin/payroll-export-config`, newConfig);
      setConfig(newConfig);
      toast.success('Configuration export paie sauvegardée');
    } catch { toast.error('Erreur de sauvegarde'); }
  };

  const toggleColumn = (key) => {
    const cols = config.columns || [];
    const newCols = cols.includes(key) ? cols.filter(c => c !== key) : [...cols, key];
    saveConfig({ ...config, columns: newCols });
  };

  const moveColumn = (key, direction) => {
    const cols = [...(config.columns || [])];
    const idx = cols.indexOf(key);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= cols.length) return;
    [cols[idx], cols[newIdx]] = [cols[newIdx], cols[idx]];
    saveConfig({ ...config, columns: cols });
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F5156]"></div></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Configuration Export Paie</h2>
        <p className="text-sm text-slate-500">Choisissez les colonnes et leur ordre pour l'export PDF/CSV de la paie</p>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-4">
              <Checkbox
                checked={config?.include_details !== false}
                onCheckedChange={v => saveConfig({ ...config, include_details: v })}
              />
              <Label>Inclure les détails par employé</Label>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Visible</TableHead>
                  <TableHead>Colonne</TableHead>
                  <TableHead>Ordre</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ALL_COLUMNS.map(col => {
                  const isActive = (config?.columns || []).includes(col.key);
                  const idx = (config?.columns || []).indexOf(col.key);
                  return (
                    <TableRow key={col.key}>
                      <TableCell>
                        <Checkbox checked={isActive} onCheckedChange={() => toggleColumn(col.key)} />
                      </TableCell>
                      <TableCell className={!isActive ? 'text-slate-400' : ''}>{col.label}</TableCell>
                      <TableCell>
                        {isActive && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveColumn(col.key, 'up')} disabled={idx === 0}>
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveColumn(col.key, 'down')} disabled={idx === (config?.columns || []).length - 1}>
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PayrollCodesConfig />
    </div>
  );
};

// --- Types de primes configurables ---
const PrimeTypesConfig = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPrime, setNewPrime] = useState({ code: '', label: '', type: 'montant' });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/prime-types`);
        setTypes(res.data || []);
      } catch { setTypes([]); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const save = async (updated) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/prime-types`, { types: updated });
      setTypes(updated);
      toast.success('Types de primes enregistrés');
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleAdd = () => {
    if (!newPrime.code || !newPrime.label) { toast.error('Code et libellé requis'); return; }
    const code = newPrime.code.toUpperCase().replace(/\s/g, '_');
    if (types.find(t => t.code === code)) { toast.error('Ce code existe déjà'); return; }
    const updated = [...types, { code, label: newPrime.label, type: newPrime.type || 'montant' }];
    save(updated);
    setNewPrime({ code: '', label: '', type: 'montant' });
  };

  const handleDelete = (code) => {
    save(types.filter(t => t.code !== code));
  };

  const handleEdit = (index, field, value) => {
    const updated = [...types];
    updated[index] = { ...updated[index], [field]: value };
    setTypes(updated);
  };

  if (loading) return <div className="flex items-center justify-center h-20"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0F5156]"></div></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><Euro className="w-4 h-4 text-[#0F5156]" />Types de primes et indemnités</CardTitle>
        <CardDescription>Configurez les types de primes disponibles dans la préparation paie. Ils apparaîtront aussi dans le mapping export paie.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Libellé</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.map((t, i) => (
              <TableRow key={t.code}>
                <TableCell className="font-mono text-sm">{t.code}</TableCell>
                <TableCell>
                  <Input
                    className="h-7 text-sm"
                    value={t.label}
                    onChange={(e) => handleEdit(i, 'label', e.target.value)}
                    onBlur={() => save(types)}
                  />
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(t.code)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex gap-2 items-end p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="space-y-1 flex-1">
            <Label className="text-xs">Code</Label>
            <Input className="h-8 font-mono text-sm" value={newPrime.code} onChange={(e) => setNewPrime({ ...newPrime, code: e.target.value })} placeholder="PRIME_SALISSURE" />
          </div>
          <div className="space-y-1 flex-1">
            <Label className="text-xs">Libellé</Label>
            <Input className="h-8 text-sm" value={newPrime.label} onChange={(e) => setNewPrime({ ...newPrime, label: e.target.value })} placeholder="Prime de salissure" />
          </div>
          <Button onClick={handleAdd} size="sm" className="bg-[#0F5156] hover:bg-[#0d464a] h-8"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
        </div>
        {saving && <p className="text-xs text-slate-500">Enregistrement...</p>}
      </CardContent>
    </Card>
  );
};

// --- Mapping codes elements variables pour export paie ---
// --- Capacités d'un logiciel de paie (checklist pilotée par PAYROLL_PROVIDERS / PayrollGateway) ---
let _payrollCapabilityLabelsCache = null;
const PayrollProviderCapabilities = ({ providerCode, providers, compact = false }) => {
  const [labels, setLabels] = useState(_payrollCapabilityLabelsCache || {});

  useEffect(() => {
    if (_payrollCapabilityLabelsCache) return;
    let cancelled = false;
    axios.get(`${API_URL}/payroll/capabilities`)
      .then((res) => {
        _payrollCapabilityLabelsCache = res.data?.all_capabilities || {};
        if (!cancelled) setLabels(_payrollCapabilityLabelsCache);
      })
      .catch(() => { /* les libellés restent les codes */ });
    return () => { cancelled = true; };
  }, []);

  const info = providers?.[providerCode];
  if (!providerCode || !info) return null;
  const caps = info.capabilities || [];
  const allCaps = Object.keys(labels).length ? Object.keys(labels) : caps;
  const isManual = providerCode === 'manual';

  return (
    <div className={`rounded-lg border ${isManual ? 'border-sky-200 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-800' : 'border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700'} p-4 space-y-3`} data-testid="provider-capabilities">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-medium">Ce que {info.name} prend en charge</p>
        {info.units && (
          <Badge variant="outline" className="text-xs">Unités acceptées : {info.units.join(', ')}</Badge>
        )}
      </div>
      {isManual && (
        <p className="text-xs text-sky-800 dark:text-sky-200">
          Mode fichier : ce logiciel n'est pas connecté. Les éléments variables sont exportés depuis Préparation paie
          (« Exporter le fichier paie », CSV ou XLSX) puis importés dans le logiciel ; les bulletins sont déposés en PDF
          dans l'onglet Bulletins. Les montants de paie viennent des bulletins déposés ou de l'estimation des charges.
        </p>
      )}
      <ul className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-x-6 gap-y-1`}>
        {allCaps.map((cap) => {
          const ok = caps.includes(cap);
          return (
            <li key={cap} className="flex items-center gap-2 text-xs">
              {ok ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
              <span className={ok ? '' : 'text-slate-400 line-through decoration-slate-300'}>{labels[cap] || cap}</span>
            </li>
          );
        })}
      </ul>
      {info.notes && <p className="text-xs text-slate-500 italic">{info.notes}</p>}
    </div>
  );
};

// --- Codes paie par fournisseur (structure_configs.payroll_codes_by_provider[<code>]) ---
const PayrollCodesConfig = () => {
  const [codes, setCodes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [provider, setProvider] = useState(null); // null = fournisseur courant
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchCodes = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/admin/payroll-codes`, { params: provider ? { provider } : {} });
        if (cancelled) return;
        setCodes(res.data.codes || []);
        setMeta(res.data);
        if (!provider && res.data.provider) setProvider(res.data.provider);
      } catch {
        if (!cancelled) { setCodes([]); setMeta(null); }
      } finally { if (!cancelled) setLoading(false); }
    };
    fetchCodes();
    return () => { cancelled = true; };
  }, [provider]);

  const saveCodes = async (updatedCodes) => {
    if (!provider) { toast.error('Sélectionnez d\'abord un logiciel de paie'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/payroll-codes`, { provider, codes: updatedCodes });
      setCodes(updatedCodes.map(c => ({ ...c, prefilled: false })));
      setMeta(prev => prev ? { ...prev, legacy: false } : prev);
      toast.success(`Codes ${meta?.available_providers?.[provider]?.name || provider} sauvegardés${res.data?.migrated ? ' (ancien mapping migré)' : ''}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  const updateCode = (index, field, value) => {
    const updated = [...codes];
    updated[index] = { ...updated[index], [field]: value, ...(field === 'provider_code' ? { prefilled: false } : {}) };
    setCodes(updated);
  };

  const TYPE_LABELS = {
    'heures': 'Heures',
    'nombre': 'Nombre',
    'montant': 'Montant (EUR)',
    'jours': 'Jours'
  };

  const TYPE_COLORS = {
    'heures': 'bg-blue-100 text-blue-800',
    'nombre': 'bg-purple-100 text-purple-800',
    'montant': 'bg-green-100 text-green-800',
    'jours': 'bg-amber-100 text-amber-800'
  };
  const TYPE_UNIT = { heures: 'HEURES', nombre: 'NOMBRE', montant: 'EUR', jours: 'JOURS' };

  const providerName = meta?.provider_name || meta?.available_providers?.[provider]?.name || provider || '';
  const units = meta?.units || [];
  const isManual = provider === 'manual';
  const isCurrent = meta?.is_current !== false;
  const unitSupported = (type) => !units.length || units.includes(TYPE_UNIT[type]);

  const configuredCount = codes.filter(c => c.provider_code && c.enabled).length;
  const totalCount = codes.length;
  const nativeCount = Object.keys(meta?.native_codes || {}).length;

  return (
    <Card className="mt-6" data-testid="payroll-codes-config">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          Codes paie
          {!loading && <span className="text-xs font-normal text-slate-500">({configuredCount}/{totalCount} configurés)</span>}
        </CardTitle>
        <CardDescription>
          Associez chaque élément Nexus SIRH au code (rubrique) correspondant dans votre logiciel de paie.
          Le mapping est enregistré par logiciel : changer de logiciel ne fait pas perdre les codes déjà saisis.
          Seuls les éléments actifs avec un code renseigné sont envoyés ou exportés.
        </CardDescription>
        <div className="flex flex-col md:flex-row md:items-center gap-3 pt-2">
          <div className="space-y-1">
            <Label className="text-xs">Logiciel de paie</Label>
            <Select value={provider || ''} onValueChange={(v) => setProvider(v)}>
              <SelectTrigger className="w-full md:w-80 h-9"><SelectValue placeholder="Sélectionnez un logiciel" /></SelectTrigger>
              <SelectContent>
                {Object.entries(meta?.available_providers || {}).map(([code, info]) => (
                  <SelectItem key={code} value={code}>
                    {info.name}{code === meta?.current_provider ? ' (logiciel actuel)' : ''}
                  </SelectItem>
                ))}
                {provider && meta?.available_providers && !meta.available_providers[provider] && (
                  <SelectItem value={provider}>{providerName}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2 md:pt-5">
            {!isCurrent && provider && (
              <Badge variant="outline" className="text-xs text-amber-700">Ce n'est pas le logiciel actuel de la structure</Badge>
            )}
            {units.length > 0 && <Badge variant="outline" className="text-xs">Unités acceptées : {units.join(', ')}</Badge>}
            {nativeCount > 0 && <Badge variant="outline" className="text-xs text-emerald-700">{nativeCount} code(s) natif(s) proposé(s)</Badge>}
          </div>
        </div>
        {meta?.legacy && (
          <p className="text-xs text-amber-700 mt-2">
            Ce mapping provient de l'ancienne configuration (commune à tous les logiciels) : il sera rattaché à {providerName} au prochain enregistrement.
          </p>
        )}
        {isManual && (
          <p className="text-xs text-sky-800 dark:text-sky-200 mt-2">
            Mode fichier : les codes saisis ici alimentent la colonne « Code » du fichier d'export (Préparation paie › « Exporter le fichier paie »),
            que vous importez ensuite dans {providerName}. Renseignez les rubriques telles qu'elles existent dans le logiciel.
          </p>
        )}
        {meta?.notes && !isManual && <p className="text-xs text-slate-500 italic mt-2">{meta.notes}</p>}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-20"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0F5156]"></div></div>
        ) : !provider ? (
          <p className="text-sm text-slate-500">Aucun logiciel de paie sélectionné : choisissez-en un ci-dessus ou configurez l'intégration dans Administration › Intégrations.</p>
        ) : (
        <>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">Actif</TableHead>
                <TableHead>Élément Nexus</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Code {providerName}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((code, i) => (
                <TableRow key={code.nexus_code} className={!code.enabled ? 'opacity-50' : ''}>
                  <TableCell>
                    <Checkbox
                      checked={code.enabled !== false}
                      onCheckedChange={(v) => updateCode(i, 'enabled', !!v)}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="font-medium text-sm">{code.label}</span>
                      <span className="text-xs text-slate-400 ml-2 font-mono">({code.nexus_code})</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_COLORS[code.type] || 'bg-slate-100 text-slate-800'}`}>
                        {TYPE_LABELS[code.type] || code.type}
                      </span>
                      {!unitSupported(code.type) && (
                        <span className="text-[11px] text-red-600" title={`${providerName} n'accepte pas l'unité ${TYPE_UNIT[code.type]}${code.type === 'jours' && units.includes('HEURES') ? ' : les jours seront convertis en heures' : ' : cet élément sera exclu de l\'envoi'}`}>
                          {code.type === 'jours' && units.includes('HEURES') ? 'converti en heures' : 'non pris en charge'}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Input
                        className={`h-8 w-40 font-mono text-sm ${code.prefilled ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20' : ''}`}
                        value={code.provider_code || ''}
                        onChange={(e) => updateCode(i, 'provider_code', e.target.value)}
                        placeholder={code.native_code ? `Natif : ${code.native_code}` : 'Ex: HS25, 1000...'}
                        disabled={code.enabled === false}
                      />
                      {code.native_code && (
                        <span className="text-[11px] text-slate-500 whitespace-nowrap" title={`Code natif proposé par ${providerName}`}>
                          {code.prefilled ? 'code natif (pré-rempli)' : `natif : ${code.native_code}`}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between mt-4 gap-4">
          <p className="text-xs text-slate-500">
            Les codes doivent correspondre exactement à ceux configurés dans {providerName}.
            {nativeCount > 0 ? ' Les codes natifs pré-remplis sont des propositions : vérifiez-les avec votre dossier de paie.' : ' Demandez à votre cabinet comptable les codes d\'éléments variables de votre dossier.'}
          </p>
          <Button onClick={() => saveCodes(codes)} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : `Enregistrer les codes ${providerName}`}
          </Button>
        </div>
        </>
        )}
      </CardContent>
    </Card>
  );
};

// --- Catégories RSE Config ---
const RSECategoriesConfig = () => {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [editKey, setEditKey] = useState(null);
  const [newCatKey, setNewCatKey] = useState('');
  const [newCatLabel, setNewCatLabel] = useState('');
  const [newCatColor, setNewCatColor] = useState('#10B981');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/rse/categories`);
        setCategories(res.data || {});
      } catch { toast.error('Erreur chargement catégories RSE'); }
      finally { setLoading(false); }
    };
    fetchCategories();
  }, []);

  const saveCategories = async (updated) => {
    try {
      await axios.put(`${API_URL}/rse/categories`, updated);
      setCategories(updated);
      toast.success('Catégories RSE mises à jour');
    } catch { toast.error('Erreur de sauvegarde'); }
  };

  const handleAddCategory = () => {
    if (!newCatKey || !newCatLabel) { toast.error('Clé et label requis'); return; }
    const key = newCatKey.toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (categories[key]) { toast.error('Cette clé existe déjà'); return; }
    const updated = {
      ...categories,
      [key]: { label: newCatLabel, color: newCatColor, subcategories: [] }
    };
    saveCategories(updated);
    setNewCatKey('');
    setNewCatLabel('');
    setNewCatColor('#10B981');
  };

  const handleDeleteCategory = (key) => {
    if (!window.confirm('Supprimer cette catégorie RSE ?')) return;
    const updated = { ...categories };
    delete updated[key];
    saveCategories(updated);
  };

  const handleUpdateLabel = (key, label) => {
    const updated = { ...categories, [key]: { ...categories[key], label } };
    saveCategories(updated);
  };

  const handleUpdateColor = (key, color) => {
    const updated = { ...categories, [key]: { ...categories[key], color } };
    saveCategories(updated);
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F5156]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Catégories RSE</h2>
          <p className="text-sm text-slate-500">Configurez les catégories pour les objectifs RSE</p>
        </div>
      </div>

      <Card>
        <CardContent className="py-4 space-y-4">
          {Object.entries(categories).map(([key, cat]) => (
            <div key={key} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <input type="color" value={cat.color || '#10B981'} onChange={e => handleUpdateColor(key, e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
              <code className="text-xs bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">{key}</code>
              <Input value={cat.label || ''} onChange={e => handleUpdateLabel(key, e.target.value)} className="flex-1 h-8" />
              <span className="text-xs text-slate-400">{(cat.subcategories || []).length} sous-cat.</span>
              <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(key)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}

          <div className="border-t pt-4 mt-4">
            <Label className="text-sm font-medium mb-2 block">Ajouter une catégorie</Label>
            <div className="flex items-center gap-3">
              <input type="color" value={newCatColor} onChange={e => setNewCatColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
              <Input value={newCatKey} onChange={e => setNewCatKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="Clé (ex: innovation)" className="w-36 h-8" />
              <Input value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)} placeholder="Label (ex: Innovation)" className="flex-1 h-8" />
              <Button onClick={handleAddCategory} className="bg-[#0F5156] hover:bg-[#0a3d41] h-8"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// --- Raccourcis Créneaux Config ---
const CreneauShortcutsConfig = () => {
  const [shortcuts, setShortcuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState(null);
  const [form, setForm] = useState({ nom: '', heure_debut: '08:00', heure_fin: '17:00', pause_minutes: 60, poste: '' });

  useEffect(() => { fetchShortcuts(); }, []);

  const fetchShortcuts = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/creneau-shortcuts`);
      setShortcuts(res.data);
    } catch { toast.error('Erreur chargement raccourcis'); }
    finally { setLoading(false); }
  };

  const resetForm = () => setForm({ nom: '', heure_debut: '08:00', heure_fin: '17:00', pause_minutes: 60, poste: '' });

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return; }
    try {
      if (editingShortcut) {
        await axios.put(`${API_URL}/admin/creneau-shortcuts/${editingShortcut.id}`, form);
        toast.success('Raccourci modifié');
      } else {
        await axios.post(`${API_URL}/admin/creneau-shortcuts`, form);
        toast.success('Raccourci créé');
      }
      setShowAddDialog(false);
      setEditingShortcut(null);
      resetForm();
      fetchShortcuts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce raccourci ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/creneau-shortcuts/${id}`);
      toast.success('Raccourci supprimé');
      fetchShortcuts();
    } catch { toast.error('Erreur suppression'); }
  };

  const openEdit = (s) => {
    setEditingShortcut(s);
    setForm({ nom: s.nom, heure_debut: s.heure_debut, heure_fin: s.heure_fin, pause_minutes: s.pause_minutes || 60, poste: s.poste || '' });
    setShowAddDialog(true);
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Raccourcis créneaux</h2>
          <p className="text-sm text-slate-500">Templates réutilisables pour pré-remplir les horaires dans le planning</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditingShortcut(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button className="gap-2"><Plus className="h-4 w-4" />Ajouter</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingShortcut ? 'Modifier le raccourci' : 'Nouveau raccourci'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label>Nom *</Label><Input value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))} placeholder="Ex: Journée standard" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Heure début</Label><Input type="time" value={form.heure_debut} onChange={e => setForm(p => ({ ...p, heure_debut: e.target.value }))} /></div>
                <div><Label>Heure fin</Label><Input type="time" value={form.heure_fin} onChange={e => setForm(p => ({ ...p, heure_fin: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Pause (minutes)</Label><Input type="number" value={form.pause_minutes} onChange={e => setForm(p => ({ ...p, pause_minutes: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label>Poste</Label><Input value={form.poste} onChange={e => setForm(p => ({ ...p, poste: e.target.value }))} placeholder="Optionnel" /></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setShowAddDialog(false); setEditingShortcut(null); resetForm(); }}>Annuler</Button>
                <Button onClick={handleSave}>{editingShortcut ? 'Modifier' : 'Créer'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {shortcuts.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">Aucun raccourci configuré. Créez-en un pour pré-remplir rapidement les horaires dans le planning.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Horaires</TableHead>
                  <TableHead>Pause</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shortcuts.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.nom}</TableCell>
                    <TableCell>{s.heure_debut} - {s.heure_fin}</TableCell>
                    <TableCell>{s.pause_minutes} min</TableCell>
                    <TableCell>{s.poste || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- Périodes de Paie Config ---
const PeriodesPaieConfig = () => {
  const [periodes, setPeriodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [editingPeriode, setEditingPeriode] = useState(null);

  const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  useEffect(() => { fetchPeriodes(); }, [selectedYear]);

  const fetchPeriodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/periodes-paie`, { params: { annee: selectedYear } });
      setPeriodes(res.data);
    } catch { toast.error('Erreur chargement périodes'); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    try {
      const res = await axios.post(`${API_URL}/admin/periodes-paie/generate`, { annee: selectedYear });
      toast.success(res.data.message);
      fetchPeriodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSavePeriode = async (mois, dateDebut, dateFin) => {
    try {
      await axios.post(`${API_URL}/admin/periodes-paie`, {
        annee: selectedYear, mois, date_debut: dateDebut, date_fin: dateFin
      });
      toast.success('Période sauvegardée');
      setEditingPeriode(null);
      fetchPeriodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette période personnalisée ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/periodes-paie/${id}`);
      toast.success('Période supprimée (retour aux dates par défaut)');
      fetchPeriodes();
    } catch { toast.error('Erreur suppression'); }
  };

  const getPeriodeForMonth = (mois) => periodes.find(p => p.mois === mois);

  const getDefaultDates = (mois) => {
    const y = selectedYear;
    const lastDay = new Date(y, mois, 0).getDate();
    return { debut: `${y}-${String(mois).padStart(2, '0')}-01`, fin: `${y}-${String(mois).padStart(2, '0')}-${lastDay}` };
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Périodes de paie</h2>
          <p className="text-sm text-slate-500">Personnalisez les dates de début et fin de chaque période mensuelle</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setSelectedYear(y => y - 1)}>←</Button>
          <span className="font-semibold text-lg">{selectedYear}</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedYear(y => y + 1)}>→</Button>
          <Button variant="outline" onClick={handleGenerate} className="gap-2 ml-4"><Plus className="h-4 w-4" />Générer par défaut</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mois</TableHead>
                <TableHead>Date début</TableHead>
                <TableHead>Date fin</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(mois => {
                const periode = getPeriodeForMonth(mois);
                const defaults = getDefaultDates(mois);
                const isEditing = editingPeriode === mois;
                const dateDebut = periode ? periode.date_debut : defaults.debut;
                const dateFin = periode ? periode.date_fin : defaults.fin;
                const isCustom = periode && (periode.date_debut !== defaults.debut || periode.date_fin !== defaults.fin);

                return (
                  <TableRow key={mois}>
                    <TableCell className="font-medium">{moisNoms[mois - 1]}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input type="date" defaultValue={dateDebut} id={`debut-${mois}`} className="w-40 h-8" />
                      ) : (
                        <span>{new Date(dateDebut + 'T00:00:00').toLocaleDateString('fr-FR')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input type="date" defaultValue={dateFin} id={`fin-${mois}`} className="w-40 h-8" />
                      ) : (
                        <span>{new Date(dateFin + 'T00:00:00').toLocaleDateString('fr-FR')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {isCustom ? (
                          <Badge className="bg-blue-100 text-blue-800">Personnalisée</Badge>
                        ) : periode ? (
                          <Badge variant="outline">Par défaut</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400">Non configurée</Badge>
                        )}
                        {periode?.validated_count > 0 && (
                          <Badge className="bg-amber-100 text-amber-800">{periode.validated_count} validée(s)</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {isEditing ? (
                          <>
                            <Button size="sm" onClick={() => {
                              const d = document.getElementById(`debut-${mois}`).value;
                              const f = document.getElementById(`fin-${mois}`).value;
                              handleSavePeriode(mois, d, f);
                            }} className="gap-1"><Save className="h-3 w-3" />Sauver</Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingPeriode(null)}>Annuler</Button>
                          </>
                        ) : (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => setEditingPeriode(mois)}><Edit className="h-4 w-4" /></Button>
                            {periode && <Button variant="ghost" size="sm" onClick={() => handleDelete(periode.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// MAIN ADMIN COMPONENT
// ============================================================
const Admin = () => {
  const { hasModuleAccess } = useAuth();
  const [activeSection, setActiveSection] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('section') || 'entreprise'; } catch { return 'entreprise'; }
  });
  const [registre, setRegistre] = useState([]);
  const [apiStatus, setApiStatus] = useState({});
  const [silaeConfig, setSilaeConfig] = useState({});
  const [structureUsers, setStructureUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [testingSilae, setTestingSilae] = useState(false);
  const [silaeTestResult, setSilaeTestResult] = useState(null);
  const [silaeDossiers, setSilaeDossiers] = useState(null);
  const [silaeSalaries, setSilaeSalaries] = useState(null);
  const [loadingSilaeDossiers, setLoadingSilaeDossiers] = useState(false);
  const [loadingSilaeSalaries, setLoadingSilaeSalaries] = useState(false);
  const [silaeMatchResults, setSilaeMatchResults] = useState(null);
  const [loadingSilaeMatch, setLoadingSilaeMatch] = useState(false);
  const [linkingSilae, setLinkingSilae] = useState({});
  const [silaeManualSelect, setSilaeManualSelect] = useState({});
  const [silaeImportSalarie, setSilaeImportSalarie] = useState({ matricule: '', employee_id: '', periode_debut: '', periode_fin: '' });
  const [importingSalarieBulletins, setImportingSalarieBulletins] = useState(false);
  const [silaeImportResult, setSilaeImportResult] = useState(null);
  const [silaeBulkImport, setSilaeBulkImport] = useState({ periode_debut: '', periode_fin: '' });
  const [importingBulkBulletins, setImportingBulkBulletins] = useState(false);
  const [silaeBulkResult, setSilaeBulkResult] = useState(null);
  const [syncingConges, setSyncingConges] = useState(false);
  const [syncCongesResult, setSyncCongesResult] = useState(null);

  // Silae REST API v1 test states
  const [silaeApiEndpoints, setSilaeApiEndpoints] = useState(null);
  const [silaePeriodeEnCours, setSilaePeriodeEnCours] = useState(null);
  const [loadingSilaePeriode, setLoadingSilaePeriode] = useState(false);
  const [silaeSelectedMatricule, setSilaeSelectedMatricule] = useState('');
  const [silaeSelectedPeriode, setSilaeSelectedPeriode] = useState(new Date().toISOString().slice(0, 7));
  const [silaeFicheSalarie, setSilaeFicheSalarie] = useState(null);
  const [loadingSilaeFiche, setLoadingSilaeFiche] = useState(false);
  const [silaeAbsences, setSilaeAbsences] = useState(null);
  const [loadingSilaeAbsences, setLoadingSilaeAbsences] = useState(false);
  const [silaeBulletinEntete, setSilaeBulletinEntete] = useState(null);
  const [loadingSilaeBulletinEntete, setLoadingSilaeBulletinEntete] = useState(false);
  const [silaeBulletinDetails, setSilaeBulletinDetails] = useState(null);
  const [loadingSilaeBulletinDetails, setLoadingSilaeBulletinDetails] = useState(false);
  const [silaeGenericResult, setSilaeGenericResult] = useState(null);
  const [loadingSilaeGeneric, setLoadingSilaeGeneric] = useState(false);

  // Structure info
  const [structureInfo, setStructureInfo] = useState(null);
  const [editingStructure, setEditingStructure] = useState(false);
  const [structureForm, setStructureForm] = useState({});
  const [savingStructure, setSavingStructure] = useState(false);

  // Payroll providers state
  const [payrollProviders, setPayrollProviders] = useState({});
  const [payrollConfig, setPayrollConfig] = useState(null);
  const [selectedPayrollProvider, setSelectedPayrollProvider] = useState('');
  const [payrollConfigForm, setPayrollConfigForm] = useState({});
  const [savingPayroll, setSavingPayroll] = useState(false);
  const [testingPayroll, setTestingPayroll] = useState(false);
  const [syncingPayroll, setSyncingPayroll] = useState(false);
  const [payrollSyncLogs, setPayrollSyncLogs] = useState([]);

  // Roles & Permissions state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ code: '', label: '', description: '', permissions: [] });
  const [savingRole, setSavingRole] = useState(false);

  // Configuration structure
  const [structureConfig, setStructureConfig] = useState({
    convention_collective_nom: '', taux_majoration_jour_ferie: 25, taux_majoration_dimanche: 50,
    taux_majoration_nuit: 10, taux_majoration_heures_sup_35_39: 10, taux_majoration_heures_sup_25: 25,
    taux_majoration_heures_sup_50: 50, montant_panier_repas: 7.62, paniers_repas_actif: true,
    journee_solidarite_date: '', journee_solidarite_annee: new Date().getFullYear(),
    journee_solidarite_travaillee: false,
    paie_jour_limite: 5, paie_verrouillage_auto: true,
    conges_mode_decompte: 'ouvres', conges_autoriser_chevauchement: false,
    conges_demi_journees: false,
    taux_charges_patronales_estime: 42, taux_charges_salariales_estime: 22
  });

  // Jours feries
  const [joursFeries, setJoursFeries] = useState([]);
  const [showJourFerieDialog, setShowJourFerieDialog] = useState(false);
  const [newJourFerie, setNewJourFerie] = useState({ date: '', nom: '' });

  // Notifications config
  const [notificationConfigs, setNotificationConfigs] = useState([]);
  const [notificationUsers, setNotificationUsers] = useState([]);
  const [alertSettings, setAlertSettings] = useState({ jours_alerte_titre_sejour: 30, jours_alerte_habilitation: 30, jours_alerte_periode_essai: 15 });
  const [smtpConfigured, setSmtpConfigured] = useState(true);
  const [sendingHabAlerts, setSendingHabAlerts] = useState(false);

  // Departements
  const [departments, setDepartments] = useState({});
  const [showAddDeptDialog, setShowAddDeptDialog] = useState(false);
  const [newDept, setNewDept] = useState({ code: '', label: '', color: '#6B7280', responsable_user_id: '' });

  // SILAE form
  const [silaeForm, setSilaeForm] = useState({ silae_client_id: '', silae_client_secret: '', silae_subscription_key: '', silae_numero_dossier: '' });

  // URSSAF DPAE config
  const [urssafConfig, setUrssafConfig] = useState({ urssaf_username: '', urssaf_password: '', urssaf_use_test: true, configured: false });
  const [savingUrssaf, setSavingUrssaf] = useState(false);
  const [testingUrssaf, setTestingUrssaf] = useState(false);

  // New user form
  const [userForm, setUserForm] = useState({ email: '', password: '', nom: '', prenom: '', role: 'rh', employee_id: '' });
  const [employees, setEmployees] = useState([]);

  // ==================== DATA FETCHING ====================
  const fetchData = useCallback(async () => {
    try {
      const [registreRes, silaeRes, usersRes, configRes, joursFeriesRes, notifConfigRes, employeesRes, structureInfoRes] = await Promise.all([
        axios.get(`${API_URL}/admin/registre`),
        axios.get(`${API_URL}/admin/silae-config`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/structure/users`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/structure/config`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/jours-feries/${new Date().getFullYear()}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/notification-config`).catch(() => ({ data: { configs: [], users: [] } })),
        axios.get(`${API_URL}/employees`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/structure/info`).catch(() => ({ data: null }))
      ]);

      setEmployees(employeesRes.data || []);
      setRegistre(registreRes.data);
      setSilaeConfig(silaeRes.data);
      setSilaeForm({ silae_client_id: silaeRes.data.silae_client_id || '', silae_client_secret: '', silae_subscription_key: '', silae_numero_dossier: silaeRes.data.silae_numero_dossier || '' });
      setStructureUsers(usersRes.data);

      if (structureInfoRes.data) {
        setStructureInfo(structureInfoRes.data);
        setStructureForm(structureInfoRes.data);
      }

      if (configRes.data) {
        setStructureConfig({
          convention_collective_nom: configRes.data.convention_collective_nom || '',
          taux_majoration_jour_ferie: configRes.data.taux_majoration_jour_ferie || 25,
          taux_majoration_dimanche: configRes.data.taux_majoration_dimanche || 50,
          taux_majoration_nuit: configRes.data.taux_majoration_nuit || 10,
          taux_majoration_heures_sup_35_39: configRes.data.taux_majoration_heures_sup_35_39 || 10,
          taux_majoration_heures_sup_25: configRes.data.taux_majoration_heures_sup_25 || 25,
          taux_majoration_heures_sup_50: configRes.data.taux_majoration_heures_sup_50 || 50,
          montant_panier_repas: configRes.data.montant_panier_repas || 7.62,
          paniers_repas_actif: configRes.data.paniers_repas_actif !== false,
          journee_solidarite_date: configRes.data.journee_solidarite_date || '',
          journee_solidarite_annee: configRes.data.journee_solidarite_annee || new Date().getFullYear(),
          journee_solidarite_travaillee: Boolean(configRes.data.journee_solidarite_travaillee),
          paie_jour_limite: configRes.data.paie_jour_limite || 5,
          paie_verrouillage_auto: Boolean(configRes.data.paie_verrouillage_auto),
          conges_mode_decompte: configRes.data.conges_mode_decompte || 'ouvres',
          conges_autoriser_chevauchement: Boolean(configRes.data.conges_autoriser_chevauchement),
          conges_demi_journees: Boolean(configRes.data.conges_demi_journees),
          taux_charges_patronales_estime: configRes.data.taux_charges_patronales_estime ?? 42,
          taux_charges_salariales_estime: configRes.data.taux_charges_salariales_estime ?? 22
        });
      }

      setJoursFeries(joursFeriesRes.data || []);
      setNotificationConfigs(notifConfigRes.data.configs || []);
      setNotificationUsers(notifConfigRes.data.users || []);
      if (notifConfigRes.data.alert_settings) setAlertSettings(notifConfigRes.data.alert_settings);
      if (typeof notifConfigRes.data.smtp_configured === 'boolean') setSmtpConfigured(notifConfigRes.data.smtp_configured);

      // Departments
      try { const deptsRes = await axios.get(`${API_URL}/admin/departments`); setDepartments(deptsRes.data || {}); } catch (err) {}

      // Payroll
      try {
        const [providersRes, payrollConfigRes, syncLogsRes] = await Promise.all([
          axios.get(`${API_URL}/payroll/providers`), axios.get(`${API_URL}/admin/payroll-config`), axios.get(`${API_URL}/payroll/sync-logs`)
        ]);
        setPayrollProviders(providersRes.data || {});
        setPayrollConfig(payrollConfigRes.data);
        if (payrollConfigRes.data.payroll_provider) { setSelectedPayrollProvider(payrollConfigRes.data.payroll_provider); setPayrollConfigForm(payrollConfigRes.data.config || {}); }
        setPayrollSyncLogs(syncLogsRes.data || []);
      } catch (err) {}

      // Roles and permissions
      try {
        const [rolesRes, permsRes] = await Promise.all([axios.get(`${API_URL}/roles`), axios.get(`${API_URL}/permissions`)]);
        setRoles(rolesRes.data || []);
        setPermissions(permsRes.data || {});
      } catch (err) {}

      // External API status
      const [mkgtRes, solidRes, mtaeRes] = await Promise.all([
        axios.get(`${API_URL}/external/mkgt/planning`).catch(() => ({ data: { status: 'error' } })),
        axios.get(`${API_URL}/external/solid/hours`).catch(() => ({ data: { status: 'error' } })),
        axios.get(`${API_URL}/external/mtae/expenses`).catch(() => ({ data: { status: 'error' } }))
      ]);
      setApiStatus({
        mkgt: mkgtRes.data, solid: solidRes.data, mtae: mtaeRes.data,
        silae: { status: silaeRes.data.silae_configured ? 'connected' : 'not_configured' }
      });
    } catch (error) { console.error('Error fetching admin data:', error); }
    finally { setLoading(false); }
  }, []);

  const fetchUrssafConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/urssaf-config`);
      setUrssafConfig({ urssaf_username: response.data.urssaf_username || '', urssaf_password: '', urssaf_use_test: response.data.urssaf_use_test !== false, configured: response.data.configured || false });
    } catch (error) {}
  };

  useEffect(() => { fetchData(); fetchUrssafConfig(); }, [fetchData]);

  // ==================== HANDLERS ====================
  const handleSaveStructureInfo = async () => {
    setSavingStructure(true);
    try {
      await axios.put(`${API_URL}/structure/info`, structureForm);
      toast.success('Informations de l\'entreprise mises à jour');
      setEditingStructure(false);
      const res = await axios.get(`${API_URL}/structure/info`);
      setStructureInfo(res.data);
      setStructureForm(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde'); }
    finally { setSavingStructure(false); }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/export/employees`);
      const emps = response.data.data;
      if (emps.length === 0) { toast.info('Aucune donnée à exporter'); return; }
      const headers = Object.keys(emps[0]).filter(k => k !== 'documents');
      const csvContent = [headers.join(';'), ...emps.map(emp => headers.map(h => emp[h] || '').join(';'))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `registre_personnel_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success('Export réussi');
    } catch (error) { toast.error('Erreur lors de l\'export'); }
  };

  const handleSaveSilaeConfig = async () => {
    try {
      const updateData = {};
      if (silaeForm.silae_client_id) updateData.silae_client_id = silaeForm.silae_client_id;
      if (silaeForm.silae_client_secret) updateData.silae_client_secret = silaeForm.silae_client_secret;
      if (silaeForm.silae_subscription_key) updateData.silae_subscription_key = silaeForm.silae_subscription_key;
      if (silaeForm.silae_numero_dossier) updateData.silae_numero_dossier = silaeForm.silae_numero_dossier;
      await axios.put(`${API_URL}/admin/silae-config`, updateData);
      toast.success('Configuration SILAE mise à jour');
      fetchData();
    } catch (error) { toast.error('Erreur lors de la mise à jour'); }
  };

  const handleTestSilae = async () => {
    setTestingSilae(true);
    setSilaeTestResult(null);
    setSilaeDossiers(null);
    setSilaeSalaries(null);
    try {
      const response = await axios.post(`${API_URL}/silae/test-connection`);
      setSilaeTestResult(response.data);
      if (response.data.success) {
        toast.success('Connexion SILAE réussie !');
      } else {
        toast.error(response.data.error || 'Échec de la connexion');
      }
    } catch (error) {
      setSilaeTestResult({ success: false, error: error.response?.data?.detail || 'Erreur de connexion SILAE' });
      toast.error(error.response?.data?.detail || 'Erreur de connexion SILAE');
    } finally { setTestingSilae(false); }
  };

  const handleListSilaeDossiers = async () => {
    setLoadingSilaeDossiers(true);
    setSilaeDossiers(null);
    try {
      const response = await axios.get(`${API_URL}/silae/dossiers`);
      setSilaeDossiers(response.data);
      if (response.data.success) {
        toast.success(`${response.data.count} dossier(s) trouvé(s)`);
      } else {
        toast.error(response.data.error || 'Erreur lors de la récupération des dossiers');
      }
    } catch (error) {
      setSilaeDossiers({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur lors de la récupération des dossiers');
    } finally { setLoadingSilaeDossiers(false); }
  };

  const handleListSilaeSalaries = async () => {
    setLoadingSilaeSalaries(true);
    setSilaeSalaries(null);
    try {
      const response = await axios.get(`${API_URL}/silae/salaries`);
      setSilaeSalaries(response.data);
      if (response.data.success) {
        const salaries = response.data.salaries;
        const count = Array.isArray(salaries) ? salaries.length : 0;
        toast.success(`${count} salarié(s) trouvé(s)`);
      } else {
        toast.error(response.data.error || 'Erreur lors de la récupération des salariés');
      }
    } catch (error) {
      setSilaeSalaries({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur lors de la récupération des salariés');
    } finally { setLoadingSilaeSalaries(false); }
  };

  const handleSilaeMatch = async () => {
    setLoadingSilaeMatch(true);
    setSilaeMatchResults(null);
    try {
      const response = await axios.get(`${API_URL}/silae/salaries/match`);
      setSilaeMatchResults(response.data);
      if (response.data.success) {
        const stats = response.data.stats || {};
        toast.success(`${stats.total_silae} salarié(s) SILAE : ${stats.linked} lié(s), ${stats.suggested} suggestion(s), ${stats.unmatched} sans correspondance`);
      } else {
        toast.error(response.data.error || 'Erreur lors du rapprochement');
      }
    } catch (error) {
      setSilaeMatchResults({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur lors du rapprochement');
    } finally { setLoadingSilaeMatch(false); }
  };

  const handleSilaeLink = async (employeeId, matriculeSilae, numeroSecu) => {
    setLinkingSilae(prev => ({ ...prev, [matriculeSilae]: true }));
    try {
      const response = await axios.post(`${API_URL}/silae/salaries/link`, {
        employee_id: employeeId,
        matricule_silae: matriculeSilae,
        numero_secu: numeroSecu || undefined
      });
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh match results
        await handleSilaeMatch();
      } else {
        toast.error(response.data.error || 'Erreur lors de la liaison');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la liaison');
    } finally {
      setLinkingSilae(prev => ({ ...prev, [matriculeSilae]: false }));
    }
  };

  const handleImportBulletinsSalarie = async () => {
    const { matricule, employee_id, periode_debut, periode_fin } = silaeImportSalarie;
    if (!matricule || !periode_debut || !periode_fin) {
      toast.error('Sélectionnez un salarié et une plage de périodes');
      return;
    }
    setImportingSalarieBulletins(true);
    setSilaeImportResult(null);
    try {
      const response = await axios.post(`${API_URL}/silae/import-bulletins-salarie`, {
        matricule_silae: matricule,
        employee_id: employee_id || undefined,
        periode_debut,
        periode_fin
      });
      setSilaeImportResult(response.data);
      if (response.data.imported_count > 0) {
        toast.success(`${response.data.imported_count} bulletin(s) importe(s) pour ${response.data.employee?.prenom} ${response.data.employee?.nom}`);
      } else {
        toast.info('Aucun nouveau bulletin à importer');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'import';
      setSilaeImportResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setImportingSalarieBulletins(false);
    }
  };

  const handleImportBulletinsTous = async () => {
    const { periode_debut, periode_fin } = silaeBulkImport;
    if (!periode_debut || !periode_fin) {
      toast.error('Sélectionnez une plage de périodes');
      return;
    }
    setImportingBulkBulletins(true);
    setSilaeBulkResult(null);
    try {
      const response = await axios.post(`${API_URL}/silae/import-bulletins-tous`, {
        periode_debut,
        periode_fin
      });
      setSilaeBulkResult(response.data);
      if (response.data.imported_count > 0) {
        const syncMsg = response.data.conges_sync ? ` + compteurs CP synchronisés (${response.data.conges_sync.synced})` : '';
        toast.success(`${response.data.imported_count} bulletin(s) ajouté(s) aux dossiers de ${response.data.employees_count} salarié(s)${syncMsg}`);
      } else {
        toast.info('Aucun nouveau bulletin à importer (dossiers déjà à jour)');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'import global';
      setSilaeBulkResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setImportingBulkBulletins(false);
    }
  };

  const handleSyncConges = async () => {
    setSyncingConges(true);
    setSyncCongesResult(null);
    try {
      const response = await axios.post(`${API_URL}/silae/sync-conges`);
      setSyncCongesResult(response.data);
      if (response.data.synced_count > 0) {
        toast.success(`Compteurs CP synchronisés pour ${response.data.synced_count} salarié(s)`);
      } else {
        toast.info(response.data.message || 'Aucun compteur synchronisé');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la synchronisation des congés';
      setSyncCongesResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setSyncingConges(false);
    }
  };

  // ---- Silae REST API v1 test handlers ----
  const handleSilaePeriodeEnCours = async () => {
    setLoadingSilaePeriode(true);
    setSilaePeriodeEnCours(null);
    try {
      const response = await axios.get(`${API_URL}/silae/periode-en-cours`);
      setSilaePeriodeEnCours(response.data);
      if (response.data.success) toast.success('Période en cours récupérée');
      else toast.error(response.data.error || 'Erreur');
    } catch (error) {
      setSilaePeriodeEnCours({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setLoadingSilaePeriode(false); }
  };

  const handleSilaeFicheSalarie = async () => {
    if (!silaeSelectedMatricule) { toast.error('Veuillez sélectionner un matricule'); return; }
    setLoadingSilaeFiche(true);
    setSilaeFicheSalarie(null);
    try {
      const response = await axios.get(`${API_URL}/silae/salarie/${silaeSelectedMatricule}/fiche`);
      setSilaeFicheSalarie(response.data);
      if (response.data.success) toast.success('Fiche salarié récupérée');
      else toast.error(response.data.error || 'Erreur');
    } catch (error) {
      setSilaeFicheSalarie({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setLoadingSilaeFiche(false); }
  };

  const handleSilaeAbsences = async () => {
    if (!silaeSelectedMatricule) { toast.error('Veuillez sélectionner un matricule'); return; }
    setLoadingSilaeAbsences(true);
    setSilaeAbsences(null);
    try {
      const year = silaeSelectedPeriode.slice(0, 4);
      const response = await axios.get(`${API_URL}/silae/salarie/${silaeSelectedMatricule}/absences?date_debut=${year}-01-01&date_fin=${year}-12-31`);
      setSilaeAbsences(response.data);
      if (response.data.success) toast.success('Absences récupérées');
      else toast.error(response.data.error || 'Erreur');
    } catch (error) {
      setSilaeAbsences({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setLoadingSilaeAbsences(false); }
  };

  const handleSilaeBulletinEntete = async () => {
    if (!silaeSelectedMatricule) { toast.error('Veuillez sélectionner un matricule'); return; }
    setLoadingSilaeBulletinEntete(true);
    setSilaeBulletinEntete(null);
    try {
      const response = await axios.get(`${API_URL}/silae/salarie/${silaeSelectedMatricule}/bulletin-entete?periode=${silaeSelectedPeriode}`);
      setSilaeBulletinEntete(response.data);
      if (response.data.success) toast.success('Bulletin en-tête récupéré');
      else toast.error(response.data.error || 'Erreur');
    } catch (error) {
      setSilaeBulletinEntete({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setLoadingSilaeBulletinEntete(false); }
  };

  const handleSilaeBulletinDetails = async () => {
    if (!silaeSelectedMatricule) { toast.error('Veuillez sélectionner un matricule'); return; }
    setLoadingSilaeBulletinDetails(true);
    setSilaeBulletinDetails(null);
    try {
      const response = await axios.get(`${API_URL}/silae/salarie/${silaeSelectedMatricule}/bulletin-details?periode=${silaeSelectedPeriode}&type_details=0`);
      setSilaeBulletinDetails(response.data);
      if (response.data.success) toast.success('Bulletin détails récupéré');
      else toast.error(response.data.error || 'Erreur');
    } catch (error) {
      setSilaeBulletinDetails({ success: false, error: error.response?.data?.detail || 'Erreur' });
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setLoadingSilaeBulletinDetails(false); }
  };

  const handleSilaeListEndpoints = async () => {
    setLoadingSilaeGeneric(true);
    setSilaeApiEndpoints(null);
    try {
      const response = await axios.get(`${API_URL}/silae/api-endpoints`);
      setSilaeApiEndpoints(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setLoadingSilaeGeneric(false); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const originUrl = window.location.origin;
      await axios.post(`${API_URL}/structure/users?origin_url=${encodeURIComponent(originUrl)}`, userForm);
      toast.success('Utilisateur créé. Un email de confirmation a été envoyé.');
      setShowUserDialog(false);
      setUserForm({ email: '', password: '', nom: '', prenom: '', role: 'rh', employee_id: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la création'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try { await axios.delete(`${API_URL}/structure/users/${userId}`); toast.success('Utilisateur supprimé'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  const handleSaveStructureConfig = async () => {
    try { await axios.put(`${API_URL}/structure/config`, structureConfig); toast.success('Configuration sauvegardée'); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde'); }
  };

  const handleAddJourFerie = async () => {
    if (!newJourFerie.date || !newJourFerie.nom) { toast.error('Veuillez remplir la date et le nom'); return; }
    try { await axios.post(`${API_URL}/jours-feries`, newJourFerie); toast.success('Jour férié ajouté'); setShowJourFerieDialog(false); setNewJourFerie({ date: '', nom: '' }); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteJourFerie = async (jfId) => {
    try { await axios.delete(`${API_URL}/jours-feries/${jfId}`); toast.success('Jour férié supprimé'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleSaveNotificationConfig = async () => {
    try {
      const notifications = notificationConfigs.map(c => ({ type_notification: c.type_notification, actif: c.actif, destinataires_roles: c.destinataires_roles || [], destinataires_users: c.destinataires_users || [], envoyer_email: c.envoyer_email, envoyer_app: c.envoyer_app }));
      await axios.put(`${API_URL}/admin/notification-config`, { notifications, alert_settings: alertSettings });
      toast.success('Configuration des notifications sauvegardée');
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde'); }
  };

  const handleSendHabilitationAlerts = async () => {
    setSendingHabAlerts(true);
    try {
      const res = await axios.post(`${API_URL}/admin/habilitations/send-alerts`);
      const { alertes_envoyees = 0, emails_envoyes = 0, smtp_configured } = res.data || {};
      if (typeof smtp_configured === 'boolean') setSmtpConfigured(smtp_configured);
      if (alertes_envoyees === 0) {
        toast.info('Aucune nouvelle alerte à envoyer (toutes les habilitations en alerte ont déjà été notifiées)');
      } else if (!smtp_configured) {
        toast.warning(`${alertes_envoyees} alerte(s) créée(s) dans l'application, aucun email envoyé : SMTP non configuré`);
      } else {
        toast.success(`${alertes_envoyees} alerte(s) envoyée(s), ${emails_envoyes} email(s)`);
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi des alertes'); }
    finally { setSendingHabAlerts(false); }
  };

  const toggleNotificationRole = (index, role) => {
    const newConfigs = [...notificationConfigs];
    const roles = newConfigs[index].destinataires_roles || [];
    newConfigs[index].destinataires_roles = roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role];
    setNotificationConfigs(newConfigs);
  };

  const handleAddDepartment = async () => {
    if (!newDept.code || !newDept.label) { toast.error('Code et nom du département requis'); return; }
    try {
      const response = await axios.post(`${API_URL}/admin/departments`, { ...newDept, responsable_user_id: newDept.responsable_user_id || null });
      setDepartments(response.data.departments);
      setNewDept({ code: '', label: '', color: '#6B7280', responsable_user_id: '' });
      setShowAddDeptDialog(false);
      toast.success('Département ajouté');
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout'); }
  };

  const handleDeleteDepartment = async (code) => {
    if (!window.confirm('Supprimer ce département ?')) return;
    try { const response = await axios.delete(`${API_URL}/admin/departments/${code}`); setDepartments(response.data.departments); toast.success('Département supprimé'); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  const handleUpdateDepartment = async (code, updates) => {
    const updatedDepts = { ...departments }; updatedDepts[code] = { ...updatedDepts[code], ...updates };
    try { await axios.put(`${API_URL}/admin/departments`, { departments: updatedDepts }); setDepartments(updatedDepts); toast.success('Département mis à jour'); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour'); }
  };

  // Payroll handlers
  const handlePayrollProviderChange = (provider) => { setSelectedPayrollProvider(provider); setPayrollConfigForm({}); };
  const handlePayrollConfigChange = (key, value) => { setPayrollConfigForm(prev => ({ ...prev, [key]: value })); };

  const handleSavePayrollConfig = async () => {
    if (!selectedPayrollProvider) { toast.error('Veuillez sélectionner un fournisseur'); return; }
    setSavingPayroll(true);
    try { await axios.put(`${API_URL}/admin/payroll-config`, { provider: selectedPayrollProvider, config: payrollConfigForm }); toast.success(`Configuration ${payrollProviders[selectedPayrollProvider]?.name} sauvegardée`); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setSavingPayroll(false); }
  };

  const handleTestPayrollConnection = async () => {
    setTestingPayroll(true);
    try { const response = await axios.post(`${API_URL}/payroll/test-connection`); response.data.success ? toast.success(`Connexion à ${response.data.provider} réussie !`) : toast.error(response.data.message || 'Échec'); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setTestingPayroll(false); }
  };

  const handleSyncPayrollEmployees = async () => {
    setSyncingPayroll(true);
    try { const response = await axios.post(`${API_URL}/payroll/sync-employees`); toast.success(`${response.data.matched_count} employés synchronisés`); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setSyncingPayroll(false); }
  };

  const handleImportPayslips = async () => {
    setSyncingPayroll(true);
    try {
      const periode = new Date().toISOString().slice(0, 7);
      const response = await axios.post(`${API_URL}/payroll/import-payslips?periode=${periode}`);
      if (response.data.job_id) {
        toast.info('Import lancé en arrière-plan. Suivez-le dans Préparation paie › Bulletins (les bulletins arrivent au statut « à vérifier »).');
      } else {
        response.data.imported_count > 0 ? toast.success(`${response.data.imported_count} bulletins importés`) : toast.info('Aucun nouveau bulletin');
      }
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setSyncingPayroll(false); }
  };

  // URSSAF handlers
  const handleSaveUrssafConfig = async () => {
    setSavingUrssaf(true);
    try { await axios.put(`${API_URL}/admin/urssaf-config`, { urssaf_username: urssafConfig.urssaf_username, urssaf_password: urssafConfig.urssaf_password || undefined, urssaf_use_test: urssafConfig.urssaf_use_test }); toast.success('Configuration URSSAF sauvegardée'); fetchUrssafConfig(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setSavingUrssaf(false); }
  };

  const handleTestUrssafConnection = async () => {
    setTestingUrssaf(true);
    try { const response = await axios.post(`${API_URL}/admin/urssaf-test`); response.data.success ? toast.success(response.data.message) : toast.error(response.data.message); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur de connexion'); }
    finally { setTestingUrssaf(false); }
  };

  // Roles handlers
  const handleOpenRoleDialog = (role = null) => {
    if (role) { setEditingRole(role); setRoleForm({ code: role.code, label: role.label, description: role.description || '', permissions: role.permissions || [] }); }
    else { setEditingRole(null); setRoleForm({ code: '', label: '', description: '', permissions: [] }); }
    setShowRoleDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.code || !roleForm.label) { toast.error('Code et nom du rôle requis'); return; }
    setSavingRole(true);
    try {
      if (editingRole) { await axios.put(`${API_URL}/roles/${editingRole.code}`, { label: roleForm.label, description: roleForm.description, permissions: roleForm.permissions }); toast.success('Rôle mis à jour'); }
      else { await axios.post(`${API_URL}/roles`, roleForm); toast.success('Rôle créé'); }
      setShowRoleDialog(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setSavingRole(false); }
  };

  const handleDeleteRole = async (roleCode) => {
    if (!window.confirm('Supprimer ce rôle ?')) return;
    try { await axios.delete(`${API_URL}/roles/${roleCode}`); toast.success('Rôle supprimé'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const togglePermission = (perm) => { setRoleForm(prev => ({ ...prev, permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm] })); };

  const getRoleLabel = (role) => ({ admin: 'Administrateur', rh: 'Ressources Humaines', exploitation: 'Exploitation', comptabilite: 'Comptabilité', employe: 'Employé' }[role] || role);

  const getApiStatusIcon = (status) => {
    if (status === 'connected') return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    if (status === 'error') return <XCircle className="w-5 h-5 text-red-500" />;
    return <AlertCircle className="w-5 h-5 text-amber-500" />;
  };

  const apis = [
    { key: 'silae', name: 'SILAE', description: 'Synchronisation paie et bulletins' },
    { key: 'mkgt', name: 'Import externe', description: 'Import données logiciel métier' },
    { key: 'solid', name: 'Solid', description: 'Heures supplémentaires et nuit' },
    { key: 'mtae', name: 'MTAE', description: 'Notes de frais' }
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div></div>;

  // ==================== SECTION RENDERERS ====================

  const renderEntreprise = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Informations de l'entreprise"
        description="Données légales et coordonnées de votre structure"
        action={
          editingStructure ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setEditingStructure(false); setStructureForm(structureInfo); }}>Annuler</Button>
              <Button onClick={handleSaveStructureInfo} disabled={savingStructure} className="bg-[#0F5156] hover:bg-[#0d464a]">
                <Save className="w-4 h-4 mr-2" />{savingStructure ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setEditingStructure(true)}><Edit className="w-4 h-4 mr-2" />Modifier</Button>
          )
        }
      />

      {/* KPI cards */}
      {structureInfo && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{structureInfo.employee_count || 0}</p>
                  <p className="text-xs text-slate-500">Employés actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center"><Shield className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{structureInfo.user_count || 0}</p>
                  <p className="text-xs text-slate-500">Utilisateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${structureInfo.status === 'active' || structureInfo.status === 'trial' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                  <CheckCircle className={`w-5 h-5 ${structureInfo.status === 'active' || structureInfo.status === 'trial' ? 'text-green-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white capitalize">{structureInfo.status === 'trial' ? 'Essai' : structureInfo.status === 'active' ? 'Actif' : structureInfo.status || '-'}</p>
                  <p className="text-xs text-slate-500">Statut</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identité - lecture seule, modifiable uniquement par SuperAdmin */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#0F5156]" />Identité
              <span className="text-xs font-normal text-slate-400 ml-auto flex items-center gap-1"><Lock className="w-3 h-3" />Modifiable par SuperAdmin uniquement</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow icon={Building2} label="Raison sociale" value={structureInfo?.nom} />
            <InfoRow icon={Hash} label="SIRET" value={structureInfo?.siret} />
            <InfoRow icon={Briefcase} label="Forme juridique" value={structureInfo?.forme_juridique} />
            <InfoRow icon={Hash} label="Code NAF" value={structureInfo?.code_naf} />
            <InfoRow icon={FileText} label="Convention collective" value={structureInfo?.convention_collective} />
          </CardContent>
        </Card>

        {/* Coordonnées */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-[#0F5156]" />Coordonnées</CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow icon={MapPin} label="Adresse" value={editingStructure ? structureForm.adresse : structureInfo?.adresse} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, adresse: e.target.value })} placeholder="Adresse" />
            <div className="flex gap-4">
              <div className="flex-1">
                <InfoRow icon={MapPin} label="Code postal" value={editingStructure ? structureForm.code_postal : structureInfo?.code_postal} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, code_postal: e.target.value })} placeholder="XXXXX" />
              </div>
              <div className="flex-1">
                <InfoRow icon={MapPin} label="Ville" value={editingStructure ? structureForm.ville : structureInfo?.ville} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, ville: e.target.value })} placeholder="Ville" />
              </div>
            </div>
            <InfoRow icon={Phone} label="Téléphone" value={editingStructure ? structureForm.telephone : structureInfo?.telephone} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, telephone: e.target.value })} placeholder="XX XX XX XX XX" type="tel" />
            <InfoRow icon={Mail} label="Email" value={editingStructure ? structureForm.email : structureInfo?.email} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, email: e.target.value })} placeholder="contact@entreprise.fr" type="email" />
          </CardContent>
        </Card>

        {/* Représentant légal */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2"><Users className="w-4 h-4 text-[#0F5156]" />Représentant légal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
              <InfoRow icon={Users} label="Nom" value={editingStructure ? structureForm.representant_nom : structureInfo?.representant_nom} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, representant_nom: e.target.value })} placeholder="Nom" />
              <InfoRow icon={Users} label="Prénom" value={editingStructure ? structureForm.representant_prenom : structureInfo?.representant_prenom} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, representant_prenom: e.target.value })} placeholder="Prénom" />
              <InfoRow icon={Briefcase} label="Qualité" value={editingStructure ? structureForm.representant_qualite : structureInfo?.representant_qualite} editable={editingStructure} onChange={(e) => setStructureForm({ ...structureForm, representant_qualite: e.target.value })} placeholder="Gérant, Président..." />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderDepartments = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Départements"
        description="Configurez les départements de votre structure"
        action={
          <Dialog open={showAddDeptDialog} onOpenChange={setShowAddDeptDialog}>
            <DialogTrigger asChild><Button className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" />Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau département</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2"><Label>Code (identifiant unique)</Label><Input value={newDept.code} onChange={(e) => setNewDept({ ...newDept, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })} placeholder="Ex: commercial, technique..." /></div>
                <div className="space-y-2"><Label>Nom du département</Label><Input value={newDept.label} onChange={(e) => setNewDept({ ...newDept, label: e.target.value })} placeholder="Ex: Service Commercial" /></div>
                <div className="space-y-2"><Label>Couleur</Label><div className="flex items-center gap-2"><input type="color" value={newDept.color} onChange={(e) => setNewDept({ ...newDept, color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" /><Input value={newDept.color} onChange={(e) => setNewDept({ ...newDept, color: e.target.value })} className="flex-1" /></div></div>
                <div className="space-y-2">
                  <Label>Responsable (validation des notes de frais)</Label>
                  <Select value={newDept.responsable_user_id || 'none'} onValueChange={(v) => setNewDept({ ...newDept, responsable_user_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {structureUsers.filter(u => u.is_active !== false).map(u => <SelectItem key={u.id} value={u.id}>{u.prenom} {u.nom} ({u.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddDepartment} className="w-full bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <Card>
        <CardContent className="p-0">
          {Object.keys(departments).length === 0 ? (
            <div className="text-center py-8 text-slate-500">Aucun département configuré. Cliquez sur "Ajouter" pour commencer.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Couleur</TableHead><TableHead>Code</TableHead><TableHead>Nom</TableHead><TableHead>Responsable</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {Object.entries(departments).map(([code, dept]) => (
                  <TableRow key={code}>
                    <TableCell><input type="color" value={dept.color || '#6B7280'} onChange={(e) => handleUpdateDepartment(code, { color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" /></TableCell>
                    <TableCell><Badge variant="outline">{code}</Badge></TableCell>
                    <TableCell><Input value={dept.label} onChange={(e) => handleUpdateDepartment(code, { label: e.target.value })} className="max-w-xs" /></TableCell>
                    <TableCell>
                      <Select value={dept.responsable_user_id || 'none'} onValueChange={(v) => handleUpdateDepartment(code, { responsable_user_id: v === 'none' ? null : v })}>
                        <SelectTrigger className="w-56"><SelectValue placeholder="Aucun" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun</SelectItem>
                          {structureUsers.filter(u => u.is_active !== false || u.id === dept.responsable_user_id).map(u => <SelectItem key={u.id} value={u.id}>{u.prenom} {u.nom} ({u.role})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => handleDeleteDepartment(code)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Utilisateurs"
        description="Gérez les accès des collaborateurs au SIRH"
        action={
          <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
            <DialogTrigger asChild><Button className="bg-[#0F5156] hover:bg-[#0d464a]"><UserPlus className="w-4 h-4 mr-2" />Nouvel utilisateur</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Créer un utilisateur</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Prénom</Label><Input value={userForm.prenom} onChange={(e) => setUserForm({ ...userForm, prenom: e.target.value })} required /></div>
                  <div className="space-y-2"><Label>Nom</Label><Input value={userForm.nom} onChange={(e) => setUserForm({ ...userForm, nom: e.target.value })} required /></div>
                </div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Mot de passe</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required minLength={8} /></div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rh">Ressources Humaines</SelectItem><SelectItem value="exploitation">Exploitation</SelectItem>
                      <SelectItem value="comptabilite">Comptabilité</SelectItem><SelectItem value="employe">Employé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Employé lié (optionnel)</Label>
                  <Select value={userForm.employee_id || 'none'} onValueChange={(v) => setUserForm({ ...userForm, employee_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner un employé" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Aucun --</SelectItem>
                      {employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom} - {emp.poste}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Lier cet utilisateur à une fiche employé permet l'accès à Mon Espace</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowUserDialog(false)}>Annuler</Button>
                  <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">Créer</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <Card>
        <CardContent className="p-0">
          {structureUsers.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucun utilisateur</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Employé lié</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {structureUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.prenom} {user.nom}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Select value={user.role} onValueChange={async (v) => {
                        if (v === 'admin' && !window.confirm('Êtes-vous sûr de vouloir attribuer le rôle Administrateur ? Ce rôle donne un accès complet à toutes les fonctionnalités.')) return;
                        try { await axios.put(`${API_URL}/structure/users/${user.id}`, { role: v }); toast.success('Rôle mis à jour'); fetchData(); }
                        catch (error) { toast.error('Erreur lors de la mise à jour du rôle'); }
                      }}>
                        <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Administrateur</SelectItem>
                          <SelectItem value="rh">Ressources Humaines</SelectItem>
                          <SelectItem value="exploitation">Exploitation</SelectItem>
                          <SelectItem value="comptabilite">Comptabilité</SelectItem>
                          <SelectItem value="employe">Employé</SelectItem>
                          {roles.filter(r => !['admin', 'rh', 'exploitation', 'comptabilite', 'employe'].includes(r.code)).map(r => (
                            <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={user.employee_id || 'none'} onValueChange={async (v) => {
                        try { await axios.put(`${API_URL}/structure/users/${user.id}`, { employee_id: v === 'none' ? null : v }); toast.success('Employé lié mis à jour'); fetchData(); }
                        catch (error) { toast.error('Erreur lors de la mise à jour'); }
                      }}>
                        <SelectTrigger className="w-48"><SelectValue placeholder="-- Aucun --" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Aucun --</SelectItem>
                          {employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{user.is_active !== false ? <Badge className="bg-green-100 text-green-800">Actif</Badge> : <Badge className="bg-red-100 text-red-800">Inactif</Badge>}</TableCell>
                    <TableCell className="text-right">{user.role !== 'admin' && <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const PERMISSION_GROUPS = [
    { label: 'Tableau de bord', keys: ['dashboard'] },
    { label: 'Employés', keys: ['employees_view', 'employees_edit'] },
    { label: 'Habilitations', keys: ['habilitations_view', 'habilitations_edit'] },
    { label: 'Congés & Absences', keys: ['absences_view', 'absences_edit', 'absences_request', 'calendar_view'] },
    { label: 'Préparation Paie', keys: ['payroll_view', 'payroll_edit', 'payroll_sync', 'bulletins_distribute'] },
    { label: 'Notes de frais', keys: ['expenses_submit', 'expenses_view', 'expenses_edit', 'expenses_validate', 'expenses_pay', 'expenses_export'] },
    { label: 'Entretiens', keys: ['interviews_view', 'interviews_edit'] },
    { label: 'Documents', keys: ['documents_view', 'documents_edit'] },
    { label: 'Formations', keys: ['formations_view', 'formations_edit'] },
    { label: 'RSE', keys: ['rse_view', 'rse_edit'] },
    { label: 'Planning', keys: ['planning_view', 'planning_edit'] },
    { label: 'Émargement', keys: ['emargement_view', 'emargement_edit'] },
    { label: 'Reporting & Audit', keys: ['reporting_view', 'audit_view'] },
    { label: 'Administration', keys: ['admin', 'users_manage', 'roles_manage', 'subscription'] },
    { label: 'Outils', keys: ['chat'] },
    { label: 'Sous-traitants', keys: ['sous_traitants_view', 'sous_traitants_edit', 'sous_traitants_control'] },
  ];

  const renderRoles = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Rôles & Permissions"
        description="Créez des rôles personnalisés et gérez les permissions"
        action={<Button onClick={() => handleOpenRoleDialog()} className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" />Nouveau rôle</Button>}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map(role => (
          <Card key={role.code} className={`${role.is_system ? 'border-blue-200 bg-blue-50/50 dark:bg-blue-950/20' : 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20'}`}>
            <CardContent className="pt-4">
              <div className="flex justify-between items-start mb-2">
                <div><h4 className="font-medium">{role.label}</h4><p className="text-xs text-slate-500">{role.code}</p></div>
                <Badge variant={role.is_system ? 'secondary' : 'default'}>{role.is_system ? 'Système' : 'Personnalisé'}</Badge>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{role.description}</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {role.permissions?.slice(0, 5).map(p => <Badge key={p} variant="outline" className="text-xs">{permissions[p]?.label || p}</Badge>)}
                {role.permissions?.length > 5 && <Badge variant="outline" className="text-xs">+{role.permissions.length - 5}</Badge>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleOpenRoleDialog(role)}><Edit className="w-3 h-3 mr-1" />Modifier</Button>
                {!role.is_system && <Button size="sm" variant="destructive" onClick={() => handleDeleteRole(role.code)}><Trash2 className="w-3 h-3" /></Button>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Permissions Matrix - groupées par module */}
      <Card>
        <CardHeader><CardTitle className="text-base">Matrice des permissions</CardTitle><CardDescription>Vue d'ensemble des accès par rôle, regroupés par module</CardDescription></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead className="w-48">Permission</TableHead>{roles.map(role => <TableHead key={role.code} className="text-center text-xs">{role.label}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {PERMISSION_GROUPS.map(group => (
                  <React.Fragment key={group.label}>
                    <TableRow className="bg-slate-100 dark:bg-slate-800">
                      <TableCell colSpan={roles.length + 1} className="font-semibold text-xs text-slate-700 dark:text-slate-300 py-1.5">{group.label}</TableCell>
                    </TableRow>
                    {group.keys.filter(k => permissions[k]).map(key => (
                      <TableRow key={key}>
                        <TableCell className="text-sm pl-6">{permissions[key]?.label || key}</TableCell>
                        {roles.map(role => <TableCell key={role.code} className="text-center">{role.permissions?.includes(key) ? <span className="text-green-600 font-bold">✓</span> : <span className="text-slate-300">-</span>}</TableCell>)}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Code du rôle *</Label><Input value={roleForm.code} onChange={(e) => setRoleForm({ ...roleForm, code: e.target.value.toLowerCase().replace(/\s/g, '_') })} placeholder="mon_role" disabled={editingRole?.is_system} /></div>
              <div><Label>Nom du rôle *</Label><Input value={roleForm.label} onChange={(e) => setRoleForm({ ...roleForm, label: e.target.value })} placeholder="Mon Rôle" disabled={editingRole?.is_system} /></div>
            </div>
            <div><Label>Description</Label><Input value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} placeholder="Description du rôle" disabled={editingRole?.is_system} /></div>
            <div>
              <Label className="mb-2 block">Permissions</Label>
              <div className="max-h-[50vh] overflow-y-auto border rounded p-3 space-y-3">
                {PERMISSION_GROUPS.map(group => (
                  <div key={group.label}>
                    <div className="flex items-center justify-between mb-1 pb-1 border-b border-slate-200 dark:border-slate-700">
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{group.label}</span>
                      <button
                        type="button"
                        className="text-[10px] text-[#0F5156] hover:underline"
                        onClick={() => {
                          const groupKeys = group.keys.filter(k => permissions[k]);
                          const allChecked = groupKeys.every(k => roleForm.permissions?.includes(k));
                          const updated = allChecked
                            ? roleForm.permissions.filter(p => !groupKeys.includes(p))
                            : [...new Set([...(roleForm.permissions || []), ...groupKeys])];
                          setRoleForm({ ...roleForm, permissions: updated });
                        }}
                      >
                        {group.keys.filter(k => permissions[k]).every(k => roleForm.permissions?.includes(k)) ? 'Tout décocher' : 'Tout cocher'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {group.keys.filter(k => permissions[k]).map(key => (
                        <label key={key} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer">
                          <input type="checkbox" checked={roleForm.permissions?.includes(key)} onChange={() => togglePermission(key)} className="rounded" />
                          <div><span className="text-sm">{permissions[key]?.label}</span></div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Annuler</Button>
              <Button onClick={handleSaveRole} disabled={savingRole} className="bg-[#0F5156]">{savingRole ? 'Enregistrement...' : 'Enregistrer'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderConfig = () => (
    <div className="space-y-6">
      <SectionHeader title="Convention & Paie" description="Convention collective, majorations, paniers repas et jours fériés" />

      {/* Convention collective + Majorations */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings className="w-4 h-4 text-[#0F5156]" />Convention collective et majorations</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2"><Label>Convention collective</Label><Input value={structureConfig.convention_collective_nom} onChange={(e) => setStructureConfig({ ...structureConfig, convention_collective_nom: e.target.value })} placeholder="Ex: Convention collective applicable" /></div>
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <Label className="text-sm font-medium mb-4 block">Taux de majoration (%)</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                ['taux_majoration_jour_ferie', 'Jour férié'], ['taux_majoration_dimanche', 'Dimanche'], ['taux_majoration_nuit', 'Nuit'],
                ['taux_majoration_heures_sup_35_39', 'H. Sup 35-39h'], ['taux_majoration_heures_sup_25', 'H. Sup 25%'], ['taux_majoration_heures_sup_50', 'H. Sup 50%']
              ].map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-xs">{label}</Label>
                  <Input type="number" value={structureConfig[key]} onChange={(e) => setStructureConfig({ ...structureConfig, [key]: parseFloat(e.target.value) })} />
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <Label className="text-sm font-medium mb-1 block">Estimation des charges (masse salariale théorique)</Label>
            <p className="text-xs text-slate-500 mb-4">Taux utilisés par le Reporting pour estimer les cotisations et le coût employeur lorsqu'aucun bulletin de paie n'est disponible.</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Taux de charges patronales estimé (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={structureConfig.taux_charges_patronales_estime ?? 42} onChange={(e) => setStructureConfig({ ...structureConfig, taux_charges_patronales_estime: parseFloat(e.target.value) })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Taux de charges salariales estimé (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={structureConfig.taux_charges_salariales_estime ?? 22} onChange={(e) => setStructureConfig({ ...structureConfig, taux_charges_salariales_estime: parseFloat(e.target.value) })} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration conges */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-[#0F5156]" />Configuration des congés</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Mode de décompte des absences</Label>
            <Select value={structureConfig.conges_mode_decompte || 'ouvres'} onValueChange={(value) => setStructureConfig({ ...structureConfig, conges_mode_decompte: value })}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ouvres">Jours ouvrés (lundi-vendredi)</SelectItem>
                <SelectItem value="ouvrables">Jours ouvrables (lundi-samedi)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {structureConfig.conges_mode_decompte === 'ouvrables'
                ? 'Les absences sont décomptées du lundi au samedi, hors jours fériés. 1 semaine = 6 jours ouvrables.'
                : 'Les absences sont décomptées du lundi au vendredi, hors jours fériés. 1 semaine = 5 jours ouvrés.'
              }
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Switch checked={structureConfig.conges_autoriser_chevauchement || false} onCheckedChange={(checked) => setStructureConfig({ ...structureConfig, conges_autoriser_chevauchement: checked })} />
                <Label>Autoriser les congés en chevauchement</Label>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {structureConfig.conges_autoriser_chevauchement
                  ? 'Les RH peuvent valider plusieurs congés sur les mêmes dates pour un même salarié.'
                  : 'La validation est bloquée si un congé est déjà validé sur la même période (un forçage reste possible).'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Switch checked={structureConfig.conges_demi_journees || false} onCheckedChange={(checked) => setStructureConfig({ ...structureConfig, conges_demi_journees: checked })} />
                <Label>Autoriser les congés en demi-journée</Label>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {structureConfig.conges_demi_journees
                  ? 'Le formulaire de congé propose « Début l\'après-midi » et « Fin le midi » (0,5 jour décompté par demi-journée).'
                  : 'Les congés sont posés en journées entières uniquement.'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Paniers repas */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Euro className="w-4 h-4 text-[#0F5156]" />Paniers repas</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Switch checked={structureConfig.paniers_repas_actif} onCheckedChange={(checked) => setStructureConfig({ ...structureConfig, paniers_repas_actif: checked })} /><Label>Activer les paniers repas</Label></div>
          </div>
          {structureConfig.paniers_repas_actif && (
            <div className="space-y-2"><Label>Montant unitaire</Label><Input type="number" step="0.01" value={structureConfig.montant_panier_repas} onChange={(e) => setStructureConfig({ ...structureConfig, montant_panier_repas: parseFloat(e.target.value) })} className="w-40" /><p className="text-xs text-slate-500">Montant par jour travaillé (déduit automatiquement en cas d'absence)</p></div>
          )}
        </CardContent>
      </Card>

      {/* Types de primes */}
      <PrimeTypesConfig />

      {/* Journée de solidarité */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-[#0F5156]" />Journée de solidarité</CardTitle>
          <CardDescription>Journée de travail supplémentaire non rémunérée (souvent le lundi de Pentecôte). Définissez la date retenue par votre entreprise.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Date ({structureConfig.journee_solidarite_annee})</Label><Input type="date" value={structureConfig.journee_solidarite_date} onChange={(e) => setStructureConfig({ ...structureConfig, journee_solidarite_date: e.target.value })} /></div>
            <div className="space-y-2"><Label>Année</Label><Input type="number" value={structureConfig.journee_solidarite_annee} onChange={(e) => setStructureConfig({ ...structureConfig, journee_solidarite_annee: parseInt(e.target.value) })} /></div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Switch checked={structureConfig.journee_solidarite_travaillee || false} onCheckedChange={(checked) => setStructureConfig({ ...structureConfig, journee_solidarite_travaillee: checked })} />
                <Label>Journée de solidarité travaillée dans l'entreprise</Label>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {structureConfig.journee_solidarite_travaillee
                  ? 'Ce jour est travaillé : il est traité comme un jour ouvré normal (un congé posé ce jour-là est décompté) et signalé dans le calendrier.'
                  : 'Ce jour est traité comme un jour férié classique (non décompté des congés).'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Jours feries */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-[#0F5156]" />Jours fériés {new Date().getFullYear()}</CardTitle><CardDescription>Jours fériés nationaux et personnalisés</CardDescription></div>
          <Dialog open={showJourFerieDialog} onOpenChange={setShowJourFerieDialog}>
            <DialogTrigger asChild><Button variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" />Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Ajouter un jour férié</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Date</Label><Input type="date" value={newJourFerie.date} onChange={(e) => setNewJourFerie({ ...newJourFerie, date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Nom</Label><Input value={newJourFerie.nom} onChange={(e) => setNewJourFerie({ ...newJourFerie, nom: e.target.value })} placeholder="Ex: Fête locale" /></div>
                <Button onClick={handleAddJourFerie} className="w-full bg-[#0F5156] hover:bg-[#0d464a]">Ajouter</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Nom</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {joursFeries.map((jf, index) => (
                <TableRow key={jf.id || index}>
                  <TableCell>{new Date(jf.date).toLocaleDateString('fr-FR')}</TableCell>
                  <TableCell>{jf.nom}</TableCell>
                  <TableCell><Badge variant={jf.est_national ? 'default' : 'secondary'}>{jf.est_national ? 'National' : 'Structure'}</Badge></TableCell>
                  <TableCell className="text-right">{!jf.est_national && jf.id && <Button variant="ghost" size="sm" onClick={() => handleDeleteJourFerie(jf.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex justify-end"><Button onClick={handleSaveStructureConfig} className="bg-[#0F5156] hover:bg-[#0d464a]"><Save className="w-4 h-4 mr-2" />Sauvegarder la configuration</Button></div>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-6">
      <SectionHeader title="Notifications" description="Définissez qui doit être notifié pour chaque type d'événement" />
      {!smtpConfigured && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300" data-testid="smtp-warning-banner">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">L'envoi d'emails est désactivé : le serveur SMTP n'est pas configuré (SuperAdmin › SMTP)</p>
            <p className="text-xs mt-1">Les notifications in-app fonctionnent normalement ; les emails (alertes habilitations, factures...) ne seront pas envoyés tant que le SMTP n'est pas renseigné.</p>
          </div>
        </div>
      )}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4 text-[#0F5156]" />Alertes habilitations</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Passe quotidienne automatique ; vous pouvez la déclencher immédiatement pour votre structure.</p>
          </div>
          <Button variant="outline" onClick={handleSendHabilitationAlerts} disabled={sendingHabAlerts} data-testid="send-hab-alerts-btn">
            <Send className="w-4 h-4 mr-2" />{sendingHabAlerts ? 'Envoi...' : 'Envoyer les alertes maintenant'}
          </Button>
        </CardHeader>
      </Card>
      <Card>
        <CardContent className="pt-6 space-y-4">
          {notificationConfigs.map((config, index) => (
            <div key={config.type_notification} className="p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div><h4 className="font-medium">{config.label}</h4><p className="text-sm text-slate-500">{config.description}</p></div>
                <Switch checked={config.actif} onCheckedChange={(checked) => { const c = [...notificationConfigs]; c[index].actif = checked; setNotificationConfigs(c); }} />
              </div>
              {config.actif && (
                <div className="space-y-4">
                  <div><Label className="text-sm mb-2 block">Destinataires par rôle</Label>
                    <div className="flex flex-wrap gap-2">
                      {['admin', 'rh', 'manager', 'exploitation'].map(role => (
                        <div key={role} className="flex items-center gap-2">
                          <Checkbox id={`${config.type_notification}-${role}`} checked={(config.destinataires_roles || []).includes(role)} onCheckedChange={() => toggleNotificationRole(index, role)} />
                          <label htmlFor={`${config.type_notification}-${role}`} className="text-sm">{getRoleLabel(role)}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Checkbox id={`${config.type_notification}-app`} checked={config.envoyer_app} onCheckedChange={(checked) => { const c = [...notificationConfigs]; c[index].envoyer_app = checked; setNotificationConfigs(c); }} />
                      <label htmlFor={`${config.type_notification}-app`} className="text-sm">Notification in-app</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id={`${config.type_notification}-email`} checked={config.envoyer_email} onCheckedChange={(checked) => { const c = [...notificationConfigs]; c[index].envoyer_email = checked; setNotificationConfigs(c); }} />
                      <label htmlFor={`${config.type_notification}-email`} className="text-sm">Email</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Delais d'alerte */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-[#0F5156]" />Délais d'alerte (jours avant expiration)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2"><Label>Titre de séjour</Label><Input type="number" min="1" max="365" value={alertSettings.jours_alerte_titre_sejour} onChange={(e) => setAlertSettings({ ...alertSettings, jours_alerte_titre_sejour: parseInt(e.target.value) || 30 })} /><p className="text-xs text-slate-500">Alerte RH avant expiration</p></div>
            <div className="space-y-2"><Label>Habilitations</Label><Input type="number" min="1" max="365" value={alertSettings.jours_alerte_habilitation} onChange={(e) => setAlertSettings({ ...alertSettings, jours_alerte_habilitation: parseInt(e.target.value) || 30 })} /><p className="text-xs text-slate-500">Alerte avant expiration</p></div>
            <div className="space-y-2"><Label>Période d'essai</Label><Input type="number" min="1" max="90" value={alertSettings.jours_alerte_periode_essai} onChange={(e) => setAlertSettings({ ...alertSettings, jours_alerte_periode_essai: parseInt(e.target.value) || 15 })} /><p className="text-xs text-slate-500">Alerte avant fin</p></div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end"><Button onClick={handleSaveNotificationConfig} className="bg-[#0F5156] hover:bg-[#0d464a]"><Save className="w-4 h-4 mr-2" />Sauvegarder les notifications</Button></div>
    </div>
  );

  const renderIntegrations = () => (
    <div className="space-y-6">
      <SectionHeader title="Intégrations" description="Connectez vos logiciels de paie et services externes" />

      {/* SILAE Direct Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4 text-[#0F5156]" />SILAE - Intégration directe</CardTitle>
          <CardDescription>Configurez vos identifiants API SILAE (Client ID, Client Secret, Clé API, Numéro dossier)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status banner */}
          <div className={`p-4 rounded-lg ${silaeConfig.silae_configured ? 'bg-green-50 dark:bg-green-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
            <div className="flex items-center gap-2">
              {silaeConfig.silae_configured ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
              <span className="font-medium">{silaeConfig.silae_configured ? 'SILAE configuré' : 'SILAE non configuré'}</span>
            </div>
          </div>

          {/* Config form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client ID <span className="text-red-500">*</span></Label>
              <Input type="text" value={silaeForm.silae_client_id} onChange={(e) => setSilaeForm({ ...silaeForm, silae_client_id: e.target.value })} placeholder="Votre Client ID SILAE" />
            </div>
            <div className="space-y-2">
              <Label>Client Secret <span className="text-red-500">*</span></Label>
              <Input type="password" value={silaeForm.silae_client_secret} onChange={(e) => setSilaeForm({ ...silaeForm, silae_client_secret: e.target.value })} placeholder={silaeConfig.silae_client_secret_configured ? '--------' : 'Votre Client Secret'} />
              {silaeConfig.silae_client_secret_configured && <p className="text-xs text-green-600">Configuré</p>}
            </div>
            <div className="space-y-2">
              <Label>Clé de configuration API <span className="text-red-500">*</span></Label>
              <Input type="password" value={silaeForm.silae_subscription_key} onChange={(e) => setSilaeForm({ ...silaeForm, silae_subscription_key: e.target.value })} placeholder={silaeConfig.silae_subscription_key_configured ? '--------' : 'Ocp-Apim-Subscription-Key'} />
              {silaeConfig.silae_subscription_key_configured && <p className="text-xs text-green-600">Configuré</p>}
            </div>
            <div className="space-y-2">
              <Label>Numéro de dossier</Label>
              <Input type="text" value={silaeForm.silae_numero_dossier} onChange={(e) => setSilaeForm({ ...silaeForm, silae_numero_dossier: e.target.value })} placeholder="Numéro de dossier SILAE" />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleSaveSilaeConfig} className="bg-[#0F5156] hover:bg-[#0d464a]"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
            <Button variant="outline" onClick={handleTestSilae} disabled={!silaeConfig.silae_configured || testingSilae}><Send className="w-4 h-4 mr-2" />{testingSilae ? 'Test en cours...' : 'Tester la connexion'}</Button>
            <Button variant="outline" onClick={handleListSilaeDossiers} disabled={!silaeConfig.silae_configured || loadingSilaeDossiers}><Download className="w-4 h-4 mr-2" />{loadingSilaeDossiers ? 'Chargement...' : 'Lister les dossiers'}</Button>
            <Button variant="outline" onClick={handleListSilaeSalaries} disabled={!silaeConfig.silae_configured || !silaeForm.silae_numero_dossier || loadingSilaeSalaries}><Users className="w-4 h-4 mr-2" />{loadingSilaeSalaries ? 'Chargement...' : 'Lister les salariés'}</Button>
            <Button variant="outline" onClick={handleSilaeMatch} disabled={!silaeConfig.silae_configured || !silaeForm.silae_numero_dossier || loadingSilaeMatch} className="border-[#0F5156] text-[#0F5156] hover:bg-[#0F5156]/10"><RefreshCw className={`w-4 h-4 mr-2 ${loadingSilaeMatch ? 'animate-spin' : ''}`} />{loadingSilaeMatch ? 'Rapprochement...' : 'Rapprocher les salariés'}</Button>
            <Button variant="outline" onClick={handleSyncConges} disabled={!silaeConfig.silae_configured || syncingConges} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"><Calendar className={`w-4 h-4 mr-2 ${syncingConges ? 'animate-spin' : ''}`} />{syncingConges ? 'Synchronisation...' : 'Synchroniser les congés'}</Button>
          </div>

          {/* Test connection result */}
          {silaeTestResult && (
            <div className={`p-4 rounded-lg border ${silaeTestResult.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
              <div className="flex items-center gap-2 mb-1">
                {silaeTestResult.success ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-red-600" />}
                <span className="font-medium">{silaeTestResult.success ? 'Connexion réussie' : 'Échec de connexion'}</span>
              </div>
              {silaeTestResult.error && <p className="text-sm text-red-600 mt-1">{silaeTestResult.error}</p>}
              {silaeTestResult.message && <p className="text-sm text-green-700 mt-1">{silaeTestResult.message}</p>}
              {silaeTestResult.dossiers_count !== undefined && <p className="text-sm text-slate-600 mt-1">{silaeTestResult.dossiers_count} dossier(s) accessible(s)</p>}
            </div>
          )}

          {/* Dossiers list */}
          {silaeDossiers && (
            <div className="border rounded-lg">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b">
                <h4 className="font-medium flex items-center gap-2"><FileText className="w-4 h-4" />Dossiers SILAE ({silaeDossiers.count || 0})</h4>
              </div>
              {silaeDossiers.success && silaeDossiers.dossiers?.length > 0 ? (
                <Table>
                  <TableHeader><TableRow><TableHead>Numéro</TableHead><TableHead>Nom</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {silaeDossiers.dossiers.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono">{d.numeroDossier}</TableCell>
                        <TableCell>{d.nomDossier}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-4 text-sm text-slate-500">
                  {silaeDossiers.error || 'Aucun dossier trouvé'}
                  {silaeDossiers.detail && <p className="mt-1 text-xs text-red-500 font-mono">{silaeDossiers.detail}</p>}
                </div>
              )}
            </div>
          )}

          {/* Salaries list */}
          {silaeSalaries && (
            <div className="border rounded-lg">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b">
                <h4 className="font-medium flex items-center gap-2"><Users className="w-4 h-4" />Salariés SILAE - Dossier {silaeForm.silae_numero_dossier} ({Array.isArray(silaeSalaries.salaries) ? silaeSalaries.salaries.length : 0})</h4>
              </div>
              {silaeSalaries.success && Array.isArray(silaeSalaries.salaries) && silaeSalaries.salaries.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Matricule</TableHead><TableHead>Nom</TableHead><TableHead>N° Sécurité Sociale</TableHead><TableHead className="hidden md:table-cell">Email</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {silaeSalaries.salaries.map((s, i) => (
                        <TableRow key={i} className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setSilaeSelectedMatricule(String(s.matriculeSalarie || s.matricule || ''))}>
                          <TableCell className="font-mono">{s.matriculeSalarie || s.matricule || '-'}</TableCell>
                          <TableCell>{s.nomAffiche || s.nomFamille || [s.nom, s.prenom].filter(Boolean).join(' ') || '-'}</TableCell>
                          <TableCell className="font-mono">{s.numeroSecuriteSociale || '-'}</TableCell>
                          <TableCell className="hidden md:table-cell">{s.mel || s.melPerso || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <p className="p-2 text-xs text-slate-500">Cliquez sur un salarié pour sélectionner son matricule pour les tests ci-dessous</p>
                </div>
              ) : (
                <div className="p-4 text-sm text-slate-500">
                  {silaeSalaries.error || 'Aucun salarié trouvé'}
                </div>
              )}
            </div>
          )}

          {/* Rapprochement SILAE / Employes structure */}
          {silaeMatchResults && (
            <div className="border rounded-lg border-[#0F5156]/30">
              <div className="p-3 bg-[#0F5156]/5 dark:bg-[#0F5156]/10 border-b border-[#0F5156]/30">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2"><Users className="w-4 h-4 text-[#0F5156]" />Rapprochement SILAE / Employés</h4>
                  {silaeMatchResults.stats && (
                    <div className="flex gap-3 text-xs">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span>{silaeMatchResults.stats.linked} lié(s)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>{silaeMatchResults.stats.suggested} suggestion(s)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span>{silaeMatchResults.stats.unmatched} sans correspondance</span>
                    </div>
                  )}
                </div>
              </div>
              {silaeMatchResults.success && Array.isArray(silaeMatchResults.results) && silaeMatchResults.results.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Matricule SILAE</TableHead>
                        <TableHead>Nom SILAE</TableHead>
                        <TableHead>N° Secu</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Employé structure</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {silaeMatchResults.results.map((r, i) => (
                        <TableRow key={i} className={r.match_type === 'matricule' ? 'bg-green-50 dark:bg-green-900/10' : r.match ? 'bg-amber-50 dark:bg-amber-900/10' : ''}>
                          <TableCell className="font-mono text-sm">{r.matriculeSalarie || '-'}</TableCell>
                          <TableCell className="font-medium">{r.nomAffiche || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{r.numeroSecuriteSociale || '-'}</TableCell>
                          <TableCell>
                            {r.match_type === 'matricule' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Lié</span>
                            ) : r.match_type === 'secu' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">N° Secu</span>
                            ) : r.match_type === 'nom' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Nom exact</span>
                            ) : r.match_type === 'approx' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">Approx.</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">Aucun</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.match_type === 'matricule' || r.match_type === 'secu' || r.match_type === 'nom' ? (
                              r.match ? (
                                <span>{r.match.nom} {r.match.prenom} {r.match.matricule_paie ? <span className="text-xs text-slate-400 ml-1">(paie: {r.match.matricule_paie})</span> : r.match.matricule ? <span className="text-xs text-slate-400 ml-1">(mat: {r.match.matricule})</span> : ''}</span>
                              ) : <span className="text-slate-400 text-xs">-</span>
                            ) : (
                              <select
                                className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-slate-800 dark:border-slate-600"
                                value={silaeManualSelect[r.matriculeSalarie] || (r.match ? r.match.id : '')}
                                onChange={(e) => setSilaeManualSelect(prev => ({ ...prev, [r.matriculeSalarie]: e.target.value }))}
                              >
                                <option value="">-- Sélectionner un employé --</option>
                                {r.match && (
                                  <option value={r.match.id}>
                                    {r.match.nom} {r.match.prenom} (suggestion){r.match.matricule_paie ? ` - paie: ${r.match.matricule_paie}` : ''}
                                  </option>
                                )}
                                {(silaeMatchResults.unlinked_employees || [])
                                  .filter(emp => !r.match || emp.id !== r.match.id)
                                  .map(emp => (
                                    <option key={emp.id} value={emp.id}>
                                      {emp.nom} {emp.prenom}{emp.poste ? ` - ${emp.poste}` : ''}{emp.matricule_paie ? ` (paie: ${emp.matricule_paie})` : ''}
                                    </option>
                                  ))
                                }
                              </select>
                            )}
                          </TableCell>
                          <TableCell>
                            {r.match_type === 'matricule' ? (
                              <span className="text-xs text-green-600 font-medium">Déjà lié</span>
                            ) : r.match_type === 'secu' || r.match_type === 'nom' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-[#0F5156] text-[#0F5156] hover:bg-[#0F5156] hover:text-white"
                                disabled={linkingSilae[r.matriculeSalarie]}
                                onClick={() => handleSilaeLink(r.match.id, r.matriculeSalarie, r.numeroSecuriteSociale)}
                              >
                                {linkingSilae[r.matriculeSalarie] ? 'Liaison...' : 'Lier'}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-[#0F5156] text-[#0F5156] hover:bg-[#0F5156] hover:text-white"
                                disabled={linkingSilae[r.matriculeSalarie] || !(silaeManualSelect[r.matriculeSalarie] || (r.match && r.match.id))}
                                onClick={() => {
                                  const selectedId = silaeManualSelect[r.matriculeSalarie] || (r.match && r.match.id);
                                  if (selectedId) handleSilaeLink(selectedId, r.matriculeSalarie, r.numeroSecuriteSociale);
                                }}
                              >
                                {linkingSilae[r.matriculeSalarie] ? 'Liaison...' : 'Lier'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-4 text-sm text-slate-500">
                  {silaeMatchResults.error || 'Aucun résultat'}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Résultat synchronisation congés */}
      {syncCongesResult && (
        <Card className={`border ${syncCongesResult.success ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/10' : 'border-red-300 bg-red-50 dark:bg-red-900/10'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className={`w-4 h-4 ${syncCongesResult.success ? 'text-emerald-600' : 'text-red-600'}`} />
              Synchronisation des compteurs de congés
            </CardTitle>
          </CardHeader>
          <CardContent>
            {syncCongesResult.error ? (
              <p className="text-sm text-red-700 dark:text-red-300">{syncCongesResult.error}</p>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-4 text-sm">
                  <span className="text-emerald-700 font-medium">{syncCongesResult.synced_count} salarié(s) synchronisé(s)</span>
                  {syncCongesResult.not_found_count > 0 && <span className="text-amber-700">{syncCongesResult.not_found_count} non lié(s)</span>}
                </div>
                {syncCongesResult.synced?.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Matricule</TableHead>
                        <TableHead>Employé</TableHead>
                        <TableHead>CP N</TableHead>
                        <TableHead>CP N-1</TableHead>
                        <TableHead>RTT</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {syncCongesResult.synced.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-sm">{s.matricule}</TableCell>
                          <TableCell className="font-medium">{s.employee}</TableCell>
                          <TableCell>{s.cp_solde_n?.toFixed(1)} j</TableCell>
                          <TableCell>{s.cp_solde_n1?.toFixed(1)} j</TableCell>
                          <TableCell>{s.rtt_solde?.toFixed(1)} j</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {syncCongesResult.not_found?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-amber-600">Voir les {syncCongesResult.not_found.length} matricule(s) non lié(s)</summary>
                    <ul className="mt-1 space-y-1 text-slate-500">
                      {syncCongesResult.not_found.map((nf, i) => (
                        <li key={i}>Matricule {nf.matricule}: CP N={nf.cp_solde_n?.toFixed(1)}, N-1={nf.cp_solde_n1?.toFixed(1)}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import bulletins par salarié */}
      {silaeConfig.silae_configured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-[#0F5156]" />Import bulletins de paie par salarié</CardTitle>
            <CardDescription>Sélectionnez un salarié lié et une plage de périodes pour importer ses bulletins (entete, details, cumuls, PDF) depuis SILAE</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="space-y-2 md:col-span-2">
                <Label>Salarié SILAE</Label>
                <select
                  className="w-full text-sm border rounded px-3 py-2 bg-white dark:bg-slate-800 dark:border-slate-600"
                  value={silaeImportSalarie.matricule}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) { setSilaeImportSalarie(prev => ({ ...prev, matricule: '', employee_id: '' })); return; }
                    const parts = val.split('|');
                    setSilaeImportSalarie(prev => ({ ...prev, matricule: parts[0], employee_id: parts[1] || '' }));
                  }}
                >
                  <option value="">-- Sélectionnez un salarié lié --</option>
                  {silaeMatchResults && silaeMatchResults.results
                    ? silaeMatchResults.results
                        .filter(r => r.match_type === 'matricule')
                        .map(r => (
                          <option key={r.matriculeSalarie} value={`${r.matriculeSalarie}|${r.match?.id || ''}`}>
                            {r.matriculeSalarie} - {r.nomAffiche} → {r.match?.nom} {r.match?.prenom}
                          </option>
                        ))
                    : silaeSalaries && Array.isArray(silaeSalaries.salaries)
                      ? silaeSalaries.salaries.map(s => (
                          <option key={s.matriculeSalarie} value={s.matriculeSalarie}>
                            {s.matriculeSalarie} - {s.nomAffiche}
                          </option>
                        ))
                      : null
                  }
                </select>
                <p className="text-xs text-slate-500">Effectuez d'abord le rapprochement pour voir les salariés liés</p>
              </div>
              <div className="space-y-2">
                <Label>Période début</Label>
                <Input type="month" value={silaeImportSalarie.periode_debut} onChange={(e) => setSilaeImportSalarie(prev => ({ ...prev, periode_debut: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Période fin</Label>
                <Input type="month" value={silaeImportSalarie.periode_fin} onChange={(e) => setSilaeImportSalarie(prev => ({ ...prev, periode_fin: e.target.value }))} />
              </div>
            </div>
            <Button
              onClick={handleImportBulletinsSalarie}
              disabled={importingSalarieBulletins || !silaeImportSalarie.matricule || !silaeImportSalarie.periode_debut || !silaeImportSalarie.periode_fin}
              className="bg-[#0F5156] hover:bg-[#0d464a]"
            >
              <Download className={`w-4 h-4 mr-2 ${importingSalarieBulletins ? 'animate-spin' : ''}`} />
              {importingSalarieBulletins ? 'Import en cours...' : 'Importer les bulletins'}
            </Button>

            {/* Import result */}
            {silaeImportResult && (
              <div className={`border rounded-lg p-4 ${silaeImportResult.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20' : 'bg-red-50 border-red-200 dark:bg-red-900/20'}`}>
                {silaeImportResult.error ? (
                  <p className="text-sm text-red-700 dark:text-red-300">{silaeImportResult.error}</p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium">{silaeImportResult.employee?.prenom} {silaeImportResult.employee?.nom}</span>
                      <span className="text-xs text-slate-500">Matricule: {silaeImportResult.employee?.matricule_silae}</span>
                      {silaeImportResult.fiche_silae && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Fiche SILAE mise à jour</span>}
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-700 font-medium">{silaeImportResult.imported_count} importé(s)</span>
                      {silaeImportResult.error_count > 0 && <span className="text-amber-700">{silaeImportResult.error_count} ignoré(s)</span>}
                    </div>
                    {silaeImportResult.imported?.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Période</TableHead>
                            <TableHead>Brut</TableHead>
                            <TableHead>Net</TableHead>
                            <TableHead>PDF</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {silaeImportResult.imported.map(b => (
                            <TableRow key={b.bulletin_id}>
                              <TableCell className="font-mono text-sm">{b.periode}</TableCell>
                              <TableCell>{b.salaire_brut ? Number(b.salaire_brut).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : '-'}</TableCell>
                              <TableCell className="font-medium">{b.salaire_net ? Number(b.salaire_net).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : '-'}</TableCell>
                              <TableCell>{b.has_pdf ? <span className="text-green-600 text-xs">PDF capturé</span> : <span className="text-slate-400 text-xs">Non dispo</span>}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {silaeImportResult.errors?.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-amber-600">Voir les {silaeImportResult.errors.length} élément(s) ignoré(s)</summary>
                        <ul className="mt-1 space-y-1 text-slate-500">
                          {silaeImportResult.errors.map((e, i) => (
                            <li key={i}>{e.periode}: {e.error}{e.raw_response ? <span className="block text-slate-400 font-mono text-[10px] break-all">{e.raw_response}</span> : null}{e.detail ? <span className="block text-slate-400 font-mono text-[10px] break-all">{e.detail}</span> : null}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mise a jour des dossiers salaries : import bulletins PDF pour tous les rapproches */}
      {silaeConfig.silae_configured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Download className="w-4 h-4 text-[#0F5156]" />Mettre à jour les dossiers salariés (bulletins PDF)</CardTitle>
            <CardDescription>Récupère depuis SILAE les bulletins de paie PDF de tous les salariés rapprochés sur la plage choisie et les ajoute à leur dossier. Les bulletins déjà présents sont ignorés (pas de doublon). Les compteurs de congés sont synchronisés à la fin.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="space-y-2">
                <Label>Période début</Label>
                <Input type="month" value={silaeBulkImport.periode_debut} onChange={(e) => setSilaeBulkImport(prev => ({ ...prev, periode_debut: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Période fin</Label>
                <Input type="month" value={silaeBulkImport.periode_fin} onChange={(e) => setSilaeBulkImport(prev => ({ ...prev, periode_fin: e.target.value }))} />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleImportBulletinsTous}
                  disabled={importingBulkBulletins || !silaeBulkImport.periode_debut || !silaeBulkImport.periode_fin}
                  className="bg-[#0F5156] hover:bg-[#0d464a] w-full"
                >
                  <Download className={`w-4 h-4 mr-2 ${importingBulkBulletins ? 'animate-spin' : ''}`} />
                  {importingBulkBulletins ? 'Import en cours...' : 'Importer pour tous les salariés rapprochés'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">L'import peut prendre plusieurs minutes selon le nombre de salariés et de mois (2 appels SILAE par bulletin).</p>

            {/* Resultat import global */}
            {silaeBulkResult && (
              <div className={`border rounded-lg p-4 ${silaeBulkResult.error ? 'bg-red-50 border-red-200 dark:bg-red-900/20' : 'bg-green-50 border-green-200 dark:bg-green-900/20'}`}>
                {silaeBulkResult.error ? (
                  <p className="text-sm text-red-700 dark:text-red-300">{silaeBulkResult.error}</p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-700 font-medium">{silaeBulkResult.imported_count} bulletin(s) ajouté(s)</span>
                      <span className="text-slate-600">{silaeBulkResult.employees_count} salarié(s) traité(s)</span>
                      {silaeBulkResult.ignored_count > 0 && <span className="text-amber-700">{silaeBulkResult.ignored_count} période(s) sans bulletin</span>}
                      {silaeBulkResult.conges_sync && <span className="text-emerald-700">Compteurs CP synchronisés ({silaeBulkResult.conges_sync.synced})</span>}
                    </div>
                    {silaeBulkResult.resultats?.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Salarié</TableHead>
                            <TableHead>Matricule</TableHead>
                            <TableHead>Bulletins ajoutés</TableHead>
                            <TableHead>Périodes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {silaeBulkResult.resultats.map((r) => (
                            <TableRow key={r.employee_id}>
                              <TableCell className="font-medium">{r.employee}</TableCell>
                              <TableCell className="font-mono text-sm">{r.matricule}</TableCell>
                              <TableCell>{r.imported_count}</TableCell>
                              <TableCell className="text-xs text-slate-500">{(r.periodes_importees || []).join(', ') || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SILAE REST API v1 - Test Panel */}
      {silaeConfig.silae_configured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4 text-[#0F5156]" />SILAE REST API v1 - Tests avancés</CardTitle>
            <CardDescription>Testez les vrais endpoints de l'API REST Silae v1. Tous les appels utilisent la methode POST vers https://payroll-api.silae.fr/payroll/v1/...</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Matricule & Period selector */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="space-y-2">
                <Label>Matricule salarié</Label>
                <Input type="text" value={silaeSelectedMatricule} onChange={(e) => setSilaeSelectedMatricule(e.target.value)} placeholder="Ex: 001, 002..." />
                <p className="text-xs text-slate-500">Listez d'abord les salariés pour obtenir les matricules</p>
              </div>
              <div className="space-y-2">
                <Label>Période (YYYY-MM)</Label>
                <Input type="month" value={silaeSelectedPeriode} onChange={(e) => setSilaeSelectedPeriode(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Actions rapides</Label>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={handleSilaePeriodeEnCours} disabled={loadingSilaePeriode}>
                    {loadingSilaePeriode ? 'Chargement...' : 'Période en cours'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleSilaeListEndpoints} disabled={loadingSilaeGeneric}>
                    Voir les endpoints
                  </Button>
                </div>
              </div>
            </div>

            {/* Periode en cours result */}
            {silaePeriodeEnCours && (
              <div className={`p-4 rounded-lg border ${silaePeriodeEnCours.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20' : 'bg-red-50 border-red-200 dark:bg-red-900/20'}`}>
                <p className="text-sm font-medium mb-1">Période en cours (DossierRecupererPeriodeEnCours)</p>
                <pre className="text-xs bg-white dark:bg-slate-900 p-3 rounded overflow-x-auto max-h-40">{JSON.stringify(silaePeriodeEnCours.data || silaePeriodeEnCours, null, 2)}</pre>
              </div>
            )}

            {/* API Endpoints list */}
            {silaeApiEndpoints && (
              <div className="border rounded-lg">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b">
                  <h4 className="font-medium">Endpoints Silae REST API v1 disponibles ({silaeApiEndpoints.total})</h4>
                  <p className="text-xs text-slate-500">Auth: {silaeApiEndpoints.auth_url}</p>
                  <p className="text-xs text-slate-500">Base: {silaeApiEndpoints.base_url}</p>
                </div>
                <div className="overflow-x-auto max-h-60 overflow-y-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Endpoint</TableHead><TableHead>Méthode</TableHead><TableHead>Path</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {silaeApiEndpoints.endpoints?.map((ep, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs font-medium">{ep.name}</TableCell>
                          <TableCell><Badge variant="outline" className="bg-blue-50 text-blue-700">POST</Badge></TableCell>
                          <TableCell className="font-mono text-xs">{ep.path}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Test buttons grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Fiche Salarié */}
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="pt-4">
                  <h4 className="font-medium text-sm mb-1">Fiche Salarié</h4>
                  <p className="text-xs text-slate-500 mb-3">LectureFicheSalarie</p>
                  <Button size="sm" variant="outline" onClick={handleSilaeFicheSalarie} disabled={!silaeSelectedMatricule || loadingSilaeFiche}>
                    <Download className="w-3 h-3 mr-1" />{loadingSilaeFiche ? 'Chargement...' : 'Récupérer'}
                  </Button>
                </CardContent>
              </Card>

              {/* Absences */}
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="pt-4">
                  <h4 className="font-medium text-sm mb-1">Absences</h4>
                  <p className="text-xs text-slate-500 mb-3">SalarieAbsencesV2</p>
                  <Button size="sm" variant="outline" onClick={handleSilaeAbsences} disabled={!silaeSelectedMatricule || loadingSilaeAbsences}>
                    <Download className="w-3 h-3 mr-1" />{loadingSilaeAbsences ? 'Chargement...' : 'Récupérer'}
                  </Button>
                </CardContent>
              </Card>

              {/* Bulletin Entete */}
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="pt-4">
                  <h4 className="font-medium text-sm mb-1">Bulletin - Entete</h4>
                  <p className="text-xs text-slate-500 mb-3">SalarieBulletinEntete</p>
                  <Button size="sm" variant="outline" onClick={handleSilaeBulletinEntete} disabled={!silaeSelectedMatricule || loadingSilaeBulletinEntete}>
                    <Download className="w-3 h-3 mr-1" />{loadingSilaeBulletinEntete ? 'Chargement...' : 'Récupérer'}
                  </Button>
                </CardContent>
              </Card>

              {/* Bulletin Details */}
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="pt-4">
                  <h4 className="font-medium text-sm mb-1">Bulletin - Détails complets</h4>
                  <p className="text-xs text-slate-500 mb-3">SalarieBulletinDetails</p>
                  <Button size="sm" variant="outline" onClick={handleSilaeBulletinDetails} disabled={!silaeSelectedMatricule || loadingSilaeBulletinDetails}>
                    <Download className="w-3 h-3 mr-1" />{loadingSilaeBulletinDetails ? 'Chargement...' : 'Récupérer'}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Results display */}
            {silaeFicheSalarie && (
              <div className="border rounded-lg">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b flex items-center justify-between">
                  <h4 className="font-medium text-sm">Fiche Salarié - Matricule {silaeSelectedMatricule}</h4>
                  <Badge variant={silaeFicheSalarie.success ? 'default' : 'destructive'}>{silaeFicheSalarie.success ? 'OK' : 'Erreur'}</Badge>
                </div>
                <pre className="p-4 text-xs overflow-x-auto max-h-80 overflow-y-auto bg-white dark:bg-slate-900">{JSON.stringify(silaeFicheSalarie.data || silaeFicheSalarie, null, 2)}</pre>
              </div>
            )}

            {silaeAbsences && (
              <div className="border rounded-lg">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b flex items-center justify-between">
                  <h4 className="font-medium text-sm">Absences - Matricule {silaeSelectedMatricule}</h4>
                  <Badge variant={silaeAbsences.success ? 'default' : 'destructive'}>{silaeAbsences.success ? 'OK' : 'Erreur'}</Badge>
                </div>
                <pre className="p-4 text-xs overflow-x-auto max-h-80 overflow-y-auto bg-white dark:bg-slate-900">{JSON.stringify(silaeAbsences.data || silaeAbsences, null, 2)}</pre>
              </div>
            )}

            {silaeBulletinEntete && (
              <div className="border rounded-lg">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b flex items-center justify-between">
                  <h4 className="font-medium text-sm">Bulletin Entete - {silaeSelectedMatricule} - {silaeSelectedPeriode}</h4>
                  <Badge variant={silaeBulletinEntete.success ? 'default' : 'destructive'}>{silaeBulletinEntete.success ? 'OK' : 'Erreur'}</Badge>
                </div>
                <pre className="p-4 text-xs overflow-x-auto max-h-80 overflow-y-auto bg-white dark:bg-slate-900">{JSON.stringify(silaeBulletinEntete.data || silaeBulletinEntete, null, 2)}</pre>
              </div>
            )}

            {silaeBulletinDetails && (
              <div className="border rounded-lg">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 border-b flex items-center justify-between">
                  <h4 className="font-medium text-sm">Bulletin Details - {silaeSelectedMatricule} - {silaeSelectedPeriode}</h4>
                  <Badge variant={silaeBulletinDetails.success ? 'default' : 'destructive'}>{silaeBulletinDetails.success ? 'OK' : 'Erreur'}</Badge>
                </div>
                <pre className="p-4 text-xs overflow-x-auto max-h-80 overflow-y-auto bg-white dark:bg-slate-900">{JSON.stringify(silaeBulletinDetails.data || silaeBulletinDetails, null, 2)}</pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payroll Provider (generic) */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4 text-[#0F5156]" />Autre logiciel de paie</CardTitle><CardDescription>Connectez un logiciel de paie (SILAE, CEGID, PayFit, ADP) ou déclarez un logiciel sans connexion API (export fichier + dépôt des bulletins PDF)</CardDescription></CardHeader>
        <CardContent className="space-y-6">
          <div className={`p-4 rounded-lg ${payrollConfig?.payroll_configured ? 'bg-green-50 dark:bg-green-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
            <div className="flex items-center gap-2">
              {payrollConfig?.payroll_configured ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
              <span className="font-medium">{payrollConfig?.payroll_configured ? `${payrollProviders[payrollConfig.payroll_provider]?.name} configuré` : 'Aucun logiciel de paie configuré'}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Logiciel de paie</Label>
            <Select value={selectedPayrollProvider} onValueChange={handlePayrollProviderChange}>
              <SelectTrigger className="w-full md:w-96"><SelectValue placeholder="Sélectionnez un logiciel" /></SelectTrigger>
              <SelectContent>{Object.entries(payrollProviders).map(([code, info]) => <SelectItem key={code} value={code}>{info.name} - {info.description}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {selectedPayrollProvider && (
            <PayrollProviderCapabilities providerCode={selectedPayrollProvider} providers={payrollProviders} />
          )}
          {selectedPayrollProvider && payrollProviders[selectedPayrollProvider] && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-4">Configuration {payrollProviders[selectedPayrollProvider].name}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {payrollProviders[selectedPayrollProvider].config_fields.map(field => (
                  <div key={field.key} className="space-y-2">
                    <Label>{field.label}{field.required && <span className="text-red-500 ml-1">*</span>}</Label>
                    <Input type={field.type === 'password' ? 'password' : 'text'} value={payrollConfigForm[field.key] || ''} onChange={(e) => handlePayrollConfigChange(field.key, e.target.value)} placeholder={field.placeholder || ''} />
                    {field.type === 'password' && payrollConfig?.config?.[`${field.key}_configured`] && <p className="text-xs text-green-600">Configuré</p>}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={handleSavePayrollConfig} disabled={savingPayroll} className="bg-[#0F5156] hover:bg-[#0d464a]">{savingPayroll ? 'Enregistrement...' : 'Enregistrer'}</Button>
                <Button variant="outline" onClick={handleTestPayrollConnection} disabled={!payrollConfig?.payroll_configured || testingPayroll}><Send className="w-4 h-4 mr-2" />{testingPayroll ? 'Test en cours...' : 'Tester la connexion'}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payroll Sync */}
      {payrollConfig?.payroll_configured && (payrollConfig?.capabilities || []).some(c => ['employees.sync', 'variables.push', 'variables.export_file', 'payslips.amounts'].includes(c)) && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#0F5156]" />Synchronisation</CardTitle></CardHeader>
          <CardContent>
            {/* Cartes pilotées par les capacités du logiciel (voir PayrollGateway.describe()) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(payrollConfig?.capabilities || []).includes('employees.sync') && (
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="pt-6 text-center">
                  <Users className="w-8 h-8 text-blue-600 mb-2 mx-auto" /><h4 className="font-medium">Synchroniser les employés</h4><p className="text-sm text-slate-500 mb-4">Récupérer les matricules</p>
                  <Button variant="outline" onClick={handleSyncPayrollEmployees} disabled={syncingPayroll}><Download className="w-4 h-4 mr-2" />Synchroniser</Button>
                </CardContent>
              </Card>
              )}
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="pt-6 text-center">
                  <Upload className="w-8 h-8 text-green-600 mb-2 mx-auto" /><h4 className="font-medium">{(payrollConfig?.capabilities || []).includes('variables.push') ? 'Envoyer les variables' : 'Exporter le fichier paie'}</h4><p className="text-sm text-slate-500 mb-4">{(payrollConfig?.capabilities || []).includes('variables.push') ? 'Éléments variables de paie' : 'Fichier CSV / XLSX à importer dans le logiciel'}</p>
                  <Button variant="outline" disabled><Upload className="w-4 h-4 mr-2" />Depuis Prépa. Paie</Button>
                </CardContent>
              </Card>
              {(payrollConfig?.capabilities || []).includes('payslips.amounts') && (
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="pt-6 text-center">
                  <FileText className="w-8 h-8 text-purple-600 mb-2 mx-auto" /><h4 className="font-medium">Importer les bulletins</h4><p className="text-sm text-slate-500 mb-4">Bulletins de paie</p>
                  <Button variant="outline" onClick={handleImportPayslips} disabled={syncingPayroll}><Download className="w-4 h-4 mr-2" />Importer</Button>
                </CardContent>
              </Card>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {payrollSyncLogs.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Historique des synchronisations</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Fournisseur</TableHead><TableHead>Résultat</TableHead></TableRow></TableHeader>
              <TableBody>
                {payrollSyncLogs.slice(0, 10).map(log => (
                  <TableRow key={log.id}>
                    <TableCell>{new Date(log.created_at).toLocaleString('fr-FR')}</TableCell>
                    <TableCell><Badge variant="outline">{({ sync_employees: 'Sync employés', push_variables: 'Envoi variables', export_preparation: 'Envoi variables', export_preparation_erreur: 'Envoi en erreur', export_file: 'Export fichier' })[log.type] || 'Import bulletins'}</Badge></TableCell>
                    <TableCell>{payrollProviders[log.provider]?.name || log.provider}</TableCell>
                    <TableCell>{log.matched_count !== undefined && `${log.matched_count} sync, ${log.unmatched_count} non trouvés`}{log.imported_count !== undefined && `${log.imported_count} importés`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* External APIs */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4 text-[#0F5156]" />APIs externes</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {apis.map(api => (
              <div key={api.key} className="flex items-center justify-between p-4 border rounded-lg">
                <div><h4 className="font-medium">{api.name}</h4><p className="text-sm text-slate-500">{api.description}</p></div>
                <div className="flex items-center gap-2">
                  <Badge variant={apiStatus[api.key]?.status === 'connected' ? 'default' : 'secondary'} className={apiStatus[api.key]?.status === 'connected' ? 'bg-green-100 text-green-800' : ''}>
                    {apiStatus[api.key]?.status === 'connected' ? 'Connecté' : apiStatus[api.key]?.status === 'not_configured' ? 'Non configuré' : 'Erreur'}
                  </Badge>
                  {getApiStatusIcon(apiStatus[api.key]?.status || 'unknown')}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* URSSAF DPAE */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-[#0F5156]" />URSSAF DPAE</CardTitle><CardDescription>Configurez vos identifiants net-entreprises pour soumettre automatiquement les DPAE</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Identifiant net-entreprises</Label><Input type="text" value={urssafConfig.urssaf_username} onChange={(e) => setUrssafConfig({ ...urssafConfig, urssaf_username: e.target.value })} placeholder="Votre identifiant" /></div>
            <div className="space-y-2"><Label>Mot de passe net-entreprises</Label><Input type="password" value={urssafConfig.urssaf_password} onChange={(e) => setUrssafConfig({ ...urssafConfig, urssaf_password: e.target.value })} placeholder={urssafConfig.configured ? '--------' : 'Votre mot de passe'} />{urssafConfig.configured && <p className="text-xs text-green-600">Configuration existante. Laissez vide pour conserver.</p>}</div>
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" id="urssaf_use_test" checked={urssafConfig.urssaf_use_test} onChange={(e) => setUrssafConfig({ ...urssafConfig, urssaf_use_test: e.target.checked })} className="h-4 w-4 text-[#0F5156] rounded border-gray-300 focus:ring-[#0F5156]" />
            <Label htmlFor="urssaf_use_test" className="text-sm cursor-pointer">Utiliser l'environnement de test URSSAF (recommandé pour les premiers tests)</Label>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSaveUrssafConfig} disabled={savingUrssaf} className="bg-[#0F5156] hover:bg-[#0d464a]">{savingUrssaf ? 'Enregistrement...' : 'Enregistrer'}</Button>
            <Button variant="outline" onClick={handleTestUrssafConnection} disabled={testingUrssaf || !urssafConfig.configured}>{testingUrssaf ? 'Test en cours...' : 'Tester la connexion'}</Button>
            {urssafConfig.configured && <Badge className="bg-green-100 text-green-800">Configuré</Badge>}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderRegistre = () => (
    <div className="space-y-6">
      <SectionHeader
        title="Registre du personnel"
        description="Registre unique du personnel et export des données"
        action={<Button onClick={handleExportCSV} className="bg-[#0F5156] hover:bg-[#0d464a]"><Download className="w-4 h-4 mr-2" />Exporter CSV</Button>}
      />
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>N</TableHead><TableHead>Nom</TableHead><TableHead>Prénom</TableHead><TableHead className="hidden md:table-cell">Date naissance</TableHead><TableHead className="hidden lg:table-cell">Poste</TableHead><TableHead className="hidden lg:table-cell">Date entrée</TableHead><TableHead>Contrat</TableHead></TableRow></TableHeader>
              <TableBody>
                {registre.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Aucun employé enregistré</TableCell></TableRow>
                ) : (
                  registre.map((emp, index) => (
                    <TableRow key={emp.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="font-medium">{emp.nom}</TableCell>
                      <TableCell>{emp.prenom}</TableCell>
                      <TableCell className="hidden md:table-cell">{emp.date_naissance ? new Date(emp.date_naissance).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell">{emp.poste || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell">{emp.date_embauche ? new Date(emp.date_embauche).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell><Badge variant="outline">{emp.type_contrat || 'CDI'}</Badge></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Content dispatcher with module access guard
  const renderContent = () => {
    // Check module access for module-gated sections
    const sectionModuleMap = { config: 'payroll', habilitations: 'habilitations', interviews: 'entretiens', 'expenses-config': 'expenses', integrations: 'payroll', 'absence-types': 'conges', 'creneau-shortcuts': 'planning', 'periodes-paie': 'payroll', sous_traitants: 'sous_traitants', charges: 'reporting' };
    const requiredModule = sectionModuleMap[activeSection];
    if (requiredModule && !hasModuleAccess(requiredModule)) {
      return renderEntreprise();
    }
    switch (activeSection) {
      case 'entreprise': return renderEntreprise();
      case 'departments': return renderDepartments();
      case 'users': return renderUsers();
      case 'roles': return renderRoles();
      case 'config': return renderConfig();
      case 'habilitations': return <HabilitationsConfig />;
      case 'document-categories': return <DocumentCategoriesConfig />;
      case 'interviews': return <InterviewTypesConfig />;
      case 'custom-fields': return <CustomFieldsConfig />;
      case 'expenses-config': return <ExpensesWorkflowConfig />;
      case 'absence-types': return <AbsenceTypesConfig />;
      case 'creneau-shortcuts': return <CreneauShortcutsConfig />;
      case 'periodes-paie': return <PeriodesPaieConfig />;
      case 'payroll-export': return <PayrollExportConfig />;
      case 'rse-categories': return <RSECategoriesConfig />;
      case 'sous_traitants': return <SousTraitantDocumentTypesConfig />;
      case 'charges': return <ChargesConfig />;
      case 'subscription': return <Subscription embedded />;
      case 'notifications': return renderNotifications();
      case 'integrations': return renderIntegrations();
      case 'registre': return renderRegistre();
      default: return renderEntreprise();
    }
  };

  // ==================== MAIN RENDER ====================
  return (
    <div className="animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Administration</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Paramétrage et configuration de votre espace SIRH</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2"><RefreshCw className="w-4 h-4" />Actualiser</Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation */}
        <nav className="lg:w-64 shrink-0">
          <div className="lg:sticky lg:top-4 space-y-6">
            {NAV_SECTIONS.map((section) => {
              const visibleItems = section.items.filter(item => !item.module || hasModuleAccess(item.module));
              if (visibleItems.length === 0) return null;
              return (
                <div key={section.label}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 px-3">{section.label}</p>
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.key;
                      return (
                        <button
                          key={item.key}
                          onClick={() => setActiveSection(item.key)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            isActive
                              ? 'bg-[#0F5156] text-white shadow-sm'
                              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                          }`}
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="truncate">{item.label}</span>
                          {isActive && <ChevronRight className="w-4 h-4 ml-auto shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default Admin;
